import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';
import UrgencyTicker from '@/components/UrgencyTicker';
import ForumExpressPay from '@/components/ForumExpressPay';
import ForumFloatingPayBar from '@/components/ForumFloatingPayBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SLUG = 'invest-uae-forum-2026';

/* ------------------------------------------------------------------
   Invest UAE Forum 2026 — flagship event landing page
   Lives on the SKI AXIS domain at /events/invest-uae-forum-2026
   Design references: capitalwealthsummit.com (dark / gold / cinematic)
------------------------------------------------------------------ */

const STYLES = `
*,*::before,*::after{box-sizing:border-box;}
.uaeforum-root{
  --ink:#07070A;--ink2:#0D0D12;--ink3:#131318;--ink4:#1A1A22;--ink5:#22222C;
  --gold:#C8A84B;--gold2:#D9BC6A;--gold3:#EDD98A;
  --gold-dim:rgba(200,168,75,0.15);--gold-line:rgba(200,168,75,0.2);--gold-subtle:rgba(200,168,75,0.07);
  --cream:#F0E8D4;--white:#FFFFFF;--mist:#B8B0A4;--stone:#706A60;--smoke:#3A3830;--alert:#1a2744;
  background:var(--ink);color:var(--white);
  font-family:'DM Sans',-apple-system,BlinkMacSystemFont,sans-serif;font-weight:400;line-height:1.6;
  -webkit-font-smoothing:antialiased;overflow-x:hidden;min-height:100vh;
}
.uaeforum-root *{margin:0;padding:0;}
.uaeforum-root .reveal{opacity:0;transform:translateY(24px);transition:opacity .8s cubic-bezier(.16,1,.3,1),transform .8s cubic-bezier(.16,1,.3,1);}
.uaeforum-root .reveal.visible{opacity:1;transform:none;}
.uaeforum-root .d1{transition-delay:.08s;}.uaeforum-root .d2{transition-delay:.16s;}.uaeforum-root .d3{transition-delay:.24s;}.uaeforum-root .d4{transition-delay:.32s;}

/* ANNOUNCE */
.uaeforum-root .announce-bar{background:var(--alert);padding:11px 48px;text-align:center;display:flex;align-items:center;justify-content:center;gap:14px;border-bottom:1px solid rgba(200,168,75,.15);position:relative;z-index:300;}
.uaeforum-root .announce-bar span{font-size:11px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;color:rgba(255,255,255,.85);}
.uaeforum-root .announce-badge{background:var(--gold);color:var(--ink);font-size:10px;font-weight:800;letter-spacing:.16em;text-transform:uppercase;padding:3px 12px;border-radius:2px;animation:badgePulse 3s ease-in-out infinite;}
@keyframes badgePulse{0%,100%{opacity:1;}50%{opacity:.8;}}
.uaeforum-root .announce-dot{width:5px;height:5px;border-radius:50%;background:var(--gold);opacity:.6;animation:blink 2.5s infinite;}
@keyframes blink{0%,100%{opacity:.6;}50%{opacity:.15;}}

/* NAV */
.uaeforum-root nav.uf-nav{position:sticky;top:0;z-index:200;padding:0 48px;background:rgba(7,7,10,.96);backdrop-filter:blur(24px);border-bottom:1px solid rgba(200,168,75,.12);}
.uaeforum-root .nav-inner{max-width:1280px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;height:70px;}
.uaeforum-root .nav-brand{display:flex;align-items:center;gap:14px;}
.uaeforum-root .nav-logo-mark{font-family:'Cormorant Display',serif;font-size:16px;font-weight:700;letter-spacing:.08em;color:var(--gold);line-height:1;}
.uaeforum-root .nav-logo-sub{font-size:9px;font-weight:500;letter-spacing:.2em;text-transform:uppercase;color:var(--stone);margin-top:3px;}
.uaeforum-root .nav-pill{display:flex;align-items:center;gap:0;background:var(--ink3);border:1px solid rgba(255,255,255,.07);border-radius:50px;padding:0 4px;height:40px;}
.uaeforum-root .nav-pill a{font-size:11px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:var(--stone);text-decoration:none;padding:0 20px;height:32px;display:flex;align-items:center;border-radius:50px;transition:all .2s;white-space:nowrap;}
.uaeforum-root .nav-pill a:hover{color:var(--cream);background:rgba(255,255,255,.05);}
.uaeforum-root .nav-pill-sep{width:1px;height:14px;background:rgba(255,255,255,.07);flex-shrink:0;}
.uaeforum-root .nav-cta{background:var(--gold);color:var(--ink) !important;font-size:10px !important;font-weight:800 !important;letter-spacing:.18em !important;padding:0 24px !important;height:36px !important;border-radius:50px !important;margin-left:8px;border:none;cursor:pointer;transition:background .2s,transform .2s,box-shadow .2s !important;}
.uaeforum-root .nav-cta:hover{background:var(--gold2);transform:scale(1.02);box-shadow:0 4px 20px rgba(200,168,75,.3);}

/* HERO */
.uaeforum-root .hero{min-height:92vh;position:relative;overflow:hidden;display:flex;align-items:flex-end;padding:0 48px 72px;background:var(--ink);}
.uaeforum-root .hero-bg{position:absolute;inset:0;pointer-events:none;}
.uaeforum-root .hero-grid{position:absolute;inset:0;background-image:linear-gradient(rgba(200,168,75,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(200,168,75,.025) 1px,transparent 1px);background-size:80px 80px;}
.uaeforum-root .hero-glow-r{position:absolute;top:0;right:0;width:55%;height:100%;background:radial-gradient(ellipse at 80% 40%,rgba(200,168,75,.06) 0%,transparent 60%);}
.uaeforum-root .hero-glow-b{position:absolute;bottom:0;left:0;width:100%;height:50%;background:linear-gradient(to top,rgba(7,7,10,.95) 0%,transparent 100%);}
.uaeforum-root .hero-photo-block{position:absolute;top:0;right:0;width:48%;height:100%;background:linear-gradient(160deg,#0F0E0A 0%,#1A1510 40%,#0C0B08 100%);overflow:hidden;}
.uaeforum-root .hero-photo-pattern{position:absolute;inset:0;background-image:radial-gradient(rgba(200,168,75,.04) 1px,transparent 1px);background-size:28px 28px;}
.uaeforum-root .hero-photo-line{position:absolute;top:0;left:0;width:2px;height:100%;background:linear-gradient(to bottom,transparent 0%,var(--gold) 20%,rgba(200,168,75,.4) 80%,transparent 100%);opacity:.4;}
.uaeforum-root .hero-mono{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-family:'Cormorant Display',serif;font-size:240px;font-weight:700;font-style:italic;color:rgba(200,168,75,.07);line-height:1;white-space:nowrap;letter-spacing:-.05em;user-select:none;}
.uaeforum-root .hero-date-badge{position:absolute;top:48px;right:64px;border:1px solid var(--gold-line);padding:16px 24px;text-align:right;background:rgba(7,7,10,.6);backdrop-filter:blur(12px);}
.uaeforum-root .hdb-label{font-size:8px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:var(--gold);opacity:.8;margin-bottom:4px;}
.uaeforum-root .hdb-val{font-size:14px;font-weight:600;color:var(--cream);letter-spacing:.04em;}
.uaeforum-root .hero-content{position:relative;z-index:10;max-width:640px;animation:heroIn 1.2s cubic-bezier(.16,1,.3,1) both;}
@keyframes heroIn{from{opacity:0;transform:translateY(32px);}to{opacity:1;transform:none;}}
.uaeforum-root .hero-eyebrow{font-size:11px;font-weight:700;letter-spacing:.3em;text-transform:uppercase;color:var(--gold);margin-bottom:20px;display:flex;align-items:center;gap:12px;}
.uaeforum-root .hero-eyebrow::before{content:'';display:block;width:32px;height:1px;background:var(--gold);opacity:.6;}
.uaeforum-root .hero-h1{font-family:'Cormorant Display',serif;font-size:clamp(58px,8.5vw,116px);font-weight:700;line-height:.9;letter-spacing:-.03em;color:var(--white);margin-bottom:28px;}
.uaeforum-root .hero-h1 em{font-style:italic;color:var(--gold);}
.uaeforum-root .hero-h1 .hero-line-block{display:block;}
.uaeforum-root .hero-desc{font-family:'Cormorant Display',serif;font-size:clamp(17px,2vw,22px);font-weight:300;font-style:italic;color:var(--mist);line-height:1.55;max-width:520px;margin-bottom:40px;}
.uaeforum-root .hero-actions{display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin-bottom:52px;}
.uaeforum-root .btn-fill{background:var(--gold);color:var(--ink);font-size:11px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;padding:16px 40px;border-radius:50px;text-decoration:none;border:none;cursor:pointer;transition:all .25s cubic-bezier(.16,1,.3,1);display:inline-flex;align-items:center;gap:8px;}
.uaeforum-root .btn-fill:hover{background:var(--gold2);transform:translateY(-2px);box-shadow:0 12px 36px rgba(200,168,75,.25);}
.uaeforum-root .btn-line{background:transparent;color:var(--mist);font-size:11px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;padding:15px 32px;border-radius:50px;border:1px solid rgba(255,255,255,.12);text-decoration:none;cursor:pointer;transition:all .25s;display:inline-flex;align-items:center;gap:8px;}
.uaeforum-root .btn-line:hover{border-color:var(--gold-line);color:var(--cream);}
.uaeforum-root .hero-bottom{display:flex;align-items:center;gap:0;border:1px solid rgba(200,168,75,.18);width:fit-content;}
.uaeforum-root .hb-cell{padding:14px 28px;border-right:1px solid rgba(200,168,75,.12);display:flex;flex-direction:column;gap:4px;}
.uaeforum-root .hb-cell:last-child{border-right:none;}
.uaeforum-root .hb-lbl{font-size:8px;font-weight:700;letter-spacing:.26em;text-transform:uppercase;color:var(--stone);}
.uaeforum-root .hb-val{font-size:13px;font-weight:600;color:var(--white);letter-spacing:.01em;}
.uaeforum-root .hb-val.g{color:var(--gold);}

/* PARTNER TICKER */
.uaeforum-root .pbar{background:var(--ink2);border-top:1px solid var(--gold-line);border-bottom:1px solid var(--gold-line);padding:14px 48px;}
.uaeforum-root .pbar-inner{max-width:1280px;margin:0 auto;display:flex;align-items:center;justify-content:center;gap:0;flex-wrap:wrap;}
.uaeforum-root .pb{padding:0 36px;border-right:1px solid rgba(200,168,75,.1);display:flex;flex-direction:column;gap:3px;text-align:center;}
.uaeforum-root .pb:last-child{border-right:none;}
.uaeforum-root .pb-r{font-size:8px;font-weight:700;letter-spacing:.26em;text-transform:uppercase;color:var(--gold);opacity:.75;}
.uaeforum-root .pb-n{font-size:12px;color:var(--mist);font-weight:400;}

/* SECTIONS shared */
.uaeforum-root .about-section,.uaeforum-root .updates-section,.uaeforum-root .why-section,.uaeforum-root .speakers-section,.uaeforum-root .agenda-section,.uaeforum-root .open-section,.uaeforum-root .sponsors-section,.uaeforum-root .faq-section{padding:110px 48px;}
.uaeforum-root .about-section{background:var(--ink2);}
.uaeforum-root .updates-section{background:var(--ink);position:relative;overflow:hidden;}
.uaeforum-root .why-section{background:var(--ink);}
.uaeforum-root .speakers-section{background:var(--ink2);}
.uaeforum-root .agenda-section{background:var(--ink);}
.uaeforum-root .open-section{background:var(--ink2);}
.uaeforum-root .sponsors-section{background:var(--ink);text-align:center;}
.uaeforum-root .faq-section{background:var(--ink2);}

.uaeforum-root .about-inner,.uaeforum-root .updates-inner,.uaeforum-root .why-inner,.uaeforum-root .spk-inner,.uaeforum-root .agenda-inner,.uaeforum-root .open-inner,.uaeforum-root .sponsors-inner,.uaeforum-root .faq-inner{max-width:1280px;margin:0 auto;}
.uaeforum-root .about-inner{display:grid;grid-template-columns:1fr 1fr;gap:100px;align-items:center;}

.uaeforum-root .about-pretag{font-size:9px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:var(--gold);margin-bottom:20px;display:flex;align-items:center;gap:10px;}
.uaeforum-root .about-pretag::before{content:'';display:block;width:24px;height:1px;background:var(--gold);opacity:.6;}
.uaeforum-root .about-h2{font-family:'Cormorant Display',serif;font-size:clamp(44px,5.5vw,70px);font-weight:700;line-height:.95;letter-spacing:-.025em;margin-bottom:28px;}
.uaeforum-root .about-h2 em{color:var(--gold);font-style:italic;}
.uaeforum-root .about-intro{font-family:'Cormorant Display',serif;font-size:20px;font-weight:600;font-style:italic;color:var(--gold);margin-bottom:18px;line-height:1.4;}
.uaeforum-root .about-p{font-size:15px;color:var(--stone);line-height:1.85;margin-bottom:16px;font-weight:300;}
.uaeforum-root .about-p strong{color:var(--mist);font-weight:500;}
.uaeforum-root .stats-grid{display:grid;grid-template-columns:1fr 1fr;gap:2px;background:var(--gold-line);border:1px solid var(--gold-line);}
.uaeforum-root .stat-box{background:var(--ink2);padding:40px 32px;text-align:center;position:relative;overflow:hidden;transition:background .3s;}
.uaeforum-root .stat-box:hover{background:var(--ink3);}
.uaeforum-root .stat-box::after{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent);opacity:0;transition:opacity .3s;}
.uaeforum-root .stat-box:hover::after{opacity:1;}
.uaeforum-root .stat-n{font-family:'Cormorant Display',serif;font-size:60px;font-weight:700;color:var(--gold);line-height:1;display:block;margin-bottom:10px;}
.uaeforum-root .stat-l{font-size:10px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--stone);}

/* UPDATES (new) — featured "intelligence drops" */
.uaeforum-root .updates-decor{position:absolute;top:-10%;right:-10%;width:520px;height:520px;background:radial-gradient(ellipse,rgba(200,168,75,.06) 0%,transparent 70%);pointer-events:none;}
.uaeforum-root .updates-list{display:grid;grid-template-columns:1fr;gap:24px;margin-top:56px;}
.uaeforum-root .update-card{display:grid;grid-template-columns:140px 1fr auto;gap:36px;align-items:flex-start;background:linear-gradient(170deg,var(--ink3) 0%,var(--ink2) 100%);border:1px solid var(--gold-line);padding:38px 40px;border-radius:2px;position:relative;overflow:hidden;transition:transform .25s,border-color .25s;}
.uaeforum-root .update-card::before{content:'';position:absolute;top:0;left:0;width:3px;height:100%;background:linear-gradient(to bottom,var(--gold),transparent);}
.uaeforum-root .update-card:hover{transform:translateY(-2px);border-color:rgba(200,168,75,.45);}
.uaeforum-root .uc-date{display:flex;flex-direction:column;gap:6px;}
.uaeforum-root .uc-date-month{font-family:'Cormorant Display',serif;font-size:32px;font-weight:600;color:var(--gold);line-height:1;font-style:italic;}
.uaeforum-root .uc-tag{font-size:9px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:var(--gold);background:var(--gold-subtle);border:1px solid var(--gold-line);padding:5px 10px;border-radius:2px;display:inline-block;width:fit-content;}
.uaeforum-root .uc-body{}
.uaeforum-root .uc-headline{font-family:'Cormorant Display',serif;font-size:clamp(26px,3vw,36px);font-weight:700;line-height:1.15;letter-spacing:-.015em;color:var(--white);margin-bottom:14px;}
.uaeforum-root .uc-headline em{font-style:italic;color:var(--gold);}
.uaeforum-root .uc-lead{font-size:14px;color:var(--mist);line-height:1.75;font-weight:300;margin-bottom:18px;max-width:680px;}
.uaeforum-root .uc-highlights{list-style:none;display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;margin-bottom:18px;}
.uaeforum-root .uc-highlights li{font-size:13px;color:var(--stone);font-weight:300;line-height:1.6;padding-left:18px;position:relative;}
.uaeforum-root .uc-highlights li::before{content:'';position:absolute;left:0;top:8px;width:8px;height:1px;background:var(--gold);opacity:.7;}
.uaeforum-root .uc-attribution{font-family:'Cormorant Display',serif;font-style:italic;font-size:13px;color:var(--gold);opacity:.85;}
.uaeforum-root .uc-cta{display:flex;flex-direction:column;align-items:flex-end;gap:10px;}
.uaeforum-root .uc-cta-pill{font-size:9px;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:var(--ink);background:var(--gold);padding:8px 14px;border-radius:50px;}

/* WHY */
.uaeforum-root .section-header{margin-bottom:64px;}
.uaeforum-root .section-header.centered{text-align:center;}
.uaeforum-root .section-header.centered .s-eyebrow{justify-content:center;}
.uaeforum-root .section-header.centered .s-eyebrow::before{display:none;}
.uaeforum-root .s-eyebrow{font-size:9px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:var(--gold);margin-bottom:16px;display:flex;align-items:center;gap:10px;}
.uaeforum-root .s-eyebrow::before{content:'';display:block;width:24px;height:1px;background:var(--gold);opacity:.6;}
.uaeforum-root .s-h2{font-family:'Cormorant Display',serif;font-size:clamp(38px,4.5vw,58px);font-weight:700;line-height:1;letter-spacing:-.02em;margin-bottom:12px;}
.uaeforum-root .s-h2 em{font-style:italic;color:var(--gold);}
.uaeforum-root .s-sub{font-family:'Cormorant Display',serif;font-size:clamp(16px,1.7vw,20px);font-style:italic;font-weight:300;color:var(--stone);line-height:1.55;max-width:600px;}
.uaeforum-root .centered .s-sub{margin:0 auto;}
.uaeforum-root .s-divider{width:44px;height:2px;background:var(--gold);opacity:.6;margin-top:20px;}
.uaeforum-root .centered .s-divider{margin:20px auto 0;}
.uaeforum-root .why-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:var(--gold-line);border:1px solid var(--gold-line);}
.uaeforum-root .why-card{background:var(--ink);padding:36px 28px;transition:background .25s;cursor:default;}
.uaeforum-root .why-card:hover{background:var(--ink3);}
.uaeforum-root .why-num{font-family:'Cormorant Display',serif;font-size:56px;font-weight:300;font-style:italic;color:var(--gold);opacity:.2;line-height:1;margin-bottom:18px;transition:opacity .25s;}
.uaeforum-root .why-card:hover .why-num{opacity:.45;}
.uaeforum-root .why-heading{font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--cream);margin-bottom:12px;line-height:1.3;}
.uaeforum-root .why-body{font-size:13px;color:var(--stone);line-height:1.75;font-weight:300;}

/* SPEAKERS */
.uaeforum-root .spk-top{display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:end;margin-bottom:60px;}
.uaeforum-root .spk-top-left .s-h2{font-size:clamp(44px,6vw,72px);}
.uaeforum-root .spk-top-right{font-family:'Cormorant Display',serif;font-size:18px;font-style:italic;font-weight:300;color:var(--stone);line-height:1.6;padding-bottom:8px;}
.uaeforum-root .spk-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:2px;background:var(--gold-line);}
.uaeforum-root .spk-card{background:var(--ink3);overflow:hidden;position:relative;cursor:default;transition:background .25s;}
.uaeforum-root .spk-card:hover{background:var(--ink4);}
.uaeforum-root .spk-portrait{width:100%;aspect-ratio:3/4;background:var(--ink3);position:relative;overflow:hidden;display:flex;align-items:center;justify-content:center;}
.uaeforum-root .spk-card:nth-child(1) .spk-portrait{background:linear-gradient(170deg,#131218 0%,#180F08 100%);}
.uaeforum-root .spk-card:nth-child(2) .spk-portrait{background:linear-gradient(170deg,#0F1218 0%,#080F18 100%);}
.uaeforum-root .spk-card:nth-child(3) .spk-portrait{background:linear-gradient(170deg,#141118 0%,#100808 100%);}
.uaeforum-root .spk-card:nth-child(4) .spk-portrait{background:linear-gradient(170deg,#0E1318 0%,#0A1210 100%);}
.uaeforum-root .spk-card:nth-child(5) .spk-portrait{background:linear-gradient(170deg,#13100F 0%,#1A1208 100%);}
.uaeforum-root .spk-card:nth-child(6) .spk-portrait{background:linear-gradient(170deg,#0F1218 0%,#090B18 100%);}
.uaeforum-root .spk-card:nth-child(7) .spk-portrait{background:linear-gradient(170deg,#131218 0%,#18100A 100%);}
.uaeforum-root .spk-card:nth-child(8) .spk-portrait{background:linear-gradient(170deg,#111318 0%,#080F14 100%);}
.uaeforum-root .spk-card:nth-child(9) .spk-portrait{background:linear-gradient(170deg,#131118 0%,#181008 100%);}
.uaeforum-root .spk-portrait-pattern{position:absolute;inset:0;background-image:radial-gradient(rgba(200,168,75,.05) 1px,transparent 1px);background-size:24px 24px;}
.uaeforum-root .spk-portrait-glow{position:absolute;width:120%;height:80%;top:60%;left:50%;transform:translate(-50%,-50%);background:radial-gradient(ellipse,rgba(200,168,75,.08) 0%,transparent 70%);pointer-events:none;}
.uaeforum-root .spk-initials{position:relative;z-index:2;font-family:'Cormorant Display',serif;font-size:72px;font-weight:700;color:var(--gold);opacity:.25;letter-spacing:-.03em;font-style:italic;transition:opacity .3s;user-select:none;}
.uaeforum-root .spk-card:hover .spk-initials{opacity:.45;}
.uaeforum-root .spk-portrait-bar{position:absolute;bottom:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,var(--gold),transparent);transform:scaleX(0);transition:transform .5s cubic-bezier(.16,1,.3,1);}
.uaeforum-root .spk-card:hover .spk-portrait-bar{transform:scaleX(1);}
.uaeforum-root .spk-photo{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:center top;z-index:3;transition:transform .6s cubic-bezier(.16,1,.3,1),filter .4s;}
.uaeforum-root .spk-card:hover .spk-photo{transform:scale(1.04);}
.uaeforum-root .spk-photo-overlay{position:absolute;inset:0;background:linear-gradient(180deg,transparent 40%,rgba(13,18,24,.55) 80%,rgba(13,18,24,.85) 100%);z-index:4;pointer-events:none;}
.uaeforum-root .spk-card.keynote{grid-column:span 2;}
.uaeforum-root .spk-card.keynote .spk-initials{font-size:110px;}
.uaeforum-root .spk-info{padding:18px 16px 22px;}
.uaeforum-root .spk-badge{font-size:8px;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:var(--gold);background:var(--gold-subtle);border:1px solid var(--gold-line);padding:3px 9px;border-radius:2px;display:inline-block;margin-bottom:10px;}
.uaeforum-root .spk-name{font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--cream);line-height:1.2;margin-bottom:5px;}
.uaeforum-root .spk-org{font-size:11px;color:var(--stone);line-height:1.5;font-weight:300;}
.uaeforum-root .spk-track{font-size:10px;font-weight:600;color:var(--gold);margin-top:10px;letter-spacing:.05em;opacity:.85;}
.uaeforum-root .spk-view{margin-top:14px;font-size:10px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:var(--gold);opacity:0;transform:translateX(-4px);transition:opacity .25s,transform .25s;border-top:1px solid rgba(200,168,75,.18);padding-top:12px;}
.uaeforum-root .spk-card[role="link"]:hover .spk-view,.uaeforum-root .spk-card[role="link"]:focus-visible .spk-view{opacity:1;transform:none;}
.uaeforum-root .spk-card[role="link"]{outline:none;}
.uaeforum-root .spk-card[role="link"]:focus-visible{box-shadow:inset 0 0 0 2px var(--gold);}

/* AGENDA */
.uaeforum-root .ag-list{max-width:960px;margin:0 auto;display:flex;flex-direction:column;gap:2px;}
.uaeforum-root .ag-row{display:flex;border:1px solid rgba(200,168,75,.1);border-radius:2px;overflow:hidden;transition:border-color .25s,box-shadow .25s;}
.uaeforum-root .ag-row:hover{border-color:var(--gold-line);box-shadow:0 2px 20px rgba(200,168,75,.05);}
.uaeforum-root .ag-row.keynote-r{border-color:rgba(200,168,75,.22);}
.uaeforum-root .ag-time-col{background:var(--ink2);padding:20px 16px;min-width:106px;flex-shrink:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;border-right:1px solid rgba(200,168,75,.1);}
.uaeforum-root .ag-row.keynote-r .ag-time-col{background:rgba(200,168,75,.06);}
.uaeforum-root .ag-time{font-size:12px;font-weight:600;color:var(--gold);text-align:center;line-height:1.3;white-space:pre-line;}
.uaeforum-root .ag-dur{font-size:8px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--gold);background:var(--gold-subtle);border:1px solid rgba(200,168,75,.15);padding:3px 8px;border-radius:2px;white-space:nowrap;}
.uaeforum-root .ag-content{background:var(--ink);padding:20px 28px;flex:1;}
.uaeforum-root .ag-row.keynote-r .ag-content{background:rgba(200,168,75,.025);}
.uaeforum-root .ag-topic{font-size:13px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--cream);margin-bottom:5px;line-height:1.3;}
.uaeforum-root .ag-who{font-size:12px;color:var(--gold);margin-bottom:6px;font-weight:400;font-style:italic;}
.uaeforum-root .ag-desc{font-size:12px;color:var(--stone);line-height:1.7;font-weight:300;}
.uaeforum-root .ag-tags{display:flex;flex-wrap:wrap;gap:5px;margin-top:10px;}
.uaeforum-root .atag{font-size:9px;color:var(--smoke);background:var(--ink3);border:1px solid rgba(255,255,255,.05);padding:3px 10px;border-radius:20px;}

/* OPEN */
.uaeforum-root .open-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:2px;background:var(--gold-line);}
.uaeforum-root .open-card{background:var(--ink2);padding:44px 32px;display:flex;flex-direction:column;gap:12px;transition:background .25s;}
.uaeforum-root .open-card:hover{background:var(--ink3);}
.uaeforum-root .open-n{font-family:'Cormorant Display',serif;font-size:64px;font-weight:300;font-style:italic;color:var(--gold);opacity:.18;line-height:1;transition:opacity .25s;}
.uaeforum-root .open-card:hover .open-n{opacity:.38;}
.uaeforum-root .open-title{font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:var(--cream);line-height:1.2;}
.uaeforum-root .open-desc{font-size:13px;color:var(--stone);line-height:1.7;font-weight:300;}
.uaeforum-root .open-cta{margin-top:6px;align-self:flex-start;font-size:10px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:var(--gold);border:1px solid var(--gold-line);padding:9px 20px;border-radius:50px;cursor:pointer;background:transparent;transition:all .2s;display:inline-block;}
.uaeforum-root .open-cta:hover{background:var(--gold);color:var(--ink);}

/* SPONSORS */
.uaeforum-root .tier-grp{margin-bottom:40px;}
.uaeforum-root .tier-lbl{font-size:9px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:var(--gold);opacity:.7;padding-bottom:12px;border-bottom:1px solid rgba(200,168,75,.12);margin-bottom:14px;text-align:left;}
.uaeforum-root .slot-row{display:flex;flex-wrap:wrap;gap:8px;}
.uaeforum-root .slot{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;font-size:10px;color:rgba(200,168,75,.25);font-style:italic;border:1px dashed rgba(200,168,75,.18);cursor:pointer;transition:all .2s;border-radius:2px;letter-spacing:.04em;background:transparent;text-align:center;padding:8px 12px;}
.uaeforum-root .slot:hover{border-color:var(--gold);color:var(--gold);background:rgba(200,168,75,.04);}
.uaeforum-root .slot.filled{border:1px solid rgba(200,168,75,.32);background:linear-gradient(135deg,rgba(200,168,75,.04) 0%,rgba(200,168,75,.08) 100%);color:var(--cream);font-style:normal;cursor:default;position:relative;overflow:hidden;}
.uaeforum-root .slot.filled::before{content:'';position:absolute;left:0;top:0;bottom:0;width:2px;background:var(--gold);}
.uaeforum-root .slot.filled:hover{border-color:var(--gold);background:linear-gradient(135deg,rgba(200,168,75,.08) 0%,rgba(200,168,75,.14) 100%);}
.uaeforum-root .slot-name{font-size:13px;font-weight:700;letter-spacing:.02em;color:var(--cream);font-family:'Cormorant Display',serif;line-height:1.15;}
.uaeforum-root .s-title .slot-name{font-size:18px;}
.uaeforum-root .s-gold .slot-name{font-size:15px;}
.uaeforum-root .slot-tag{font-size:8px;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:var(--gold);font-style:normal;}
.uaeforum-root .s-title{width:200px;height:68px;}.uaeforum-root .s-gold{width:152px;height:56px;}.uaeforum-root .s-silver{width:124px;height:48px;}.uaeforum-root .s-partner{width:106px;height:42px;}

/* FAQ */
.uaeforum-root .faq-layout{display:grid;grid-template-columns:1fr 1.5fr;gap:80px;align-items:start;}
.uaeforum-root .faq-left .s-h2{font-size:clamp(40px,5vw,60px);}
.uaeforum-root .faq-list{display:flex;flex-direction:column;}
.uaeforum-root .faq-item{border-bottom:1px solid rgba(255,255,255,.07);}
.uaeforum-root .faq-q{padding:20px 0;font-size:15px;font-weight:500;color:var(--mist);cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:20px;transition:color .2s;letter-spacing:.01em;background:transparent;border:none;text-align:left;width:100%;}
.uaeforum-root .faq-q:hover{color:var(--cream);}
.uaeforum-root .faq-icon{width:26px;height:26px;border-radius:50%;border:1px solid rgba(200,168,75,.25);display:flex;align-items:center;justify-content:center;flex-shrink:0;color:var(--gold);font-size:16px;font-weight:300;transition:all .25s;}
.uaeforum-root .faq-item.open .faq-icon{background:var(--gold);color:var(--ink);border-color:var(--gold);}
.uaeforum-root .faq-a{padding:0 0 20px;font-size:14px;color:var(--stone);line-height:1.8;font-weight:300;}

/* LEARN + AUDIENCE (Split section: What You'll Learn / Who Should Attend) */
.uaeforum-root .learn-section{padding:110px 48px;background:var(--ink2);}
.uaeforum-root .learn-inner{max-width:1280px;margin:0 auto;display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--gold-line);border:1px solid var(--gold-line);}
.uaeforum-root .learn-col{background:var(--ink);padding:52px 44px;}
.uaeforum-root .learn-col + .learn-col{background:var(--ink3);}
.uaeforum-root .learn-col .s-h2{font-size:clamp(34px,3.6vw,48px);}
.uaeforum-root .learn-list{margin:28px 0 0;padding:0;list-style:none;display:flex;flex-direction:column;gap:14px;}
.uaeforum-root .learn-item{display:flex;align-items:flex-start;gap:14px;padding:16px 18px;background:rgba(200,168,75,.025);border:1px solid rgba(200,168,75,.08);border-left:2px solid var(--gold);transition:background .2s,border-color .2s;}
.uaeforum-root .learn-item:hover{background:rgba(200,168,75,.06);border-color:var(--gold-line);}
.uaeforum-root .learn-num{font-family:'Cormorant Display',serif;font-style:italic;font-weight:700;color:var(--gold);font-size:18px;flex-shrink:0;line-height:1.3;min-width:28px;}
.uaeforum-root .learn-text{font-size:13.5px;color:var(--mist);line-height:1.7;font-weight:300;}
.uaeforum-root .learn-text strong{color:var(--cream);font-weight:600;}

/* ORGANIZER (About SKI Axis / SKI APAC credibility block) */
.uaeforum-root .org-section{padding:110px 48px;background:var(--ink);position:relative;overflow:hidden;}
.uaeforum-root .org-section::before{content:'';position:absolute;top:0;left:50%;width:1px;height:60px;background:linear-gradient(to bottom,var(--gold),transparent);}
.uaeforum-root .org-inner{max-width:1280px;margin:0 auto;}
.uaeforum-root .org-grid{display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:start;}
.uaeforum-root .org-card{background:var(--ink2);border:1px solid var(--gold-line);padding:42px 38px;position:relative;}
.uaeforum-root .org-card::before{content:'';position:absolute;left:0;top:24px;bottom:24px;width:2px;background:var(--gold);}
.uaeforum-root .org-eyebrow{font-size:9px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:var(--gold);margin-bottom:14px;}
.uaeforum-root .org-name{font-family:'Cormorant Display',serif;font-size:30px;font-weight:700;color:var(--cream);line-height:1.1;letter-spacing:-.01em;margin:0 0 8px;}
.uaeforum-root .org-name em{font-style:italic;color:var(--gold);}
.uaeforum-root .org-tagline{font-family:'Cormorant Display',serif;font-style:italic;font-weight:300;font-size:16px;color:var(--stone);line-height:1.55;margin-bottom:22px;}
.uaeforum-root .org-body{font-size:13.5px;color:var(--mist);line-height:1.85;font-weight:300;margin:0 0 22px;}
.uaeforum-root .org-stats{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--gold-line);border:1px solid var(--gold-line);margin-top:24px;}
.uaeforum-root .org-stat{background:var(--ink);padding:18px 16px;}
.uaeforum-root .org-stat-v{font-family:'Cormorant Display',serif;font-size:30px;font-weight:700;color:var(--gold);line-height:1;margin-bottom:4px;font-style:italic;}
.uaeforum-root .org-stat-l{font-size:9px;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:var(--smoke);}
.uaeforum-root .org-link{display:inline-flex;align-items:center;gap:8px;margin-top:8px;font-size:10px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:var(--gold);text-decoration:none;border-bottom:1px solid var(--gold-line);padding-bottom:4px;}
.uaeforum-root .org-link:hover{color:var(--cream);border-color:var(--cream);}

/* UPCOMING (Cross-promo strip) */
.uaeforum-root .upcoming-section{padding:90px 48px;background:var(--ink2);}
.uaeforum-root .upcoming-inner{max-width:1280px;margin:0 auto;}
.uaeforum-root .upcoming-head{display:flex;justify-content:space-between;align-items:flex-end;flex-wrap:wrap;gap:24px;margin-bottom:48px;}
.uaeforum-root .upcoming-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:2px;background:var(--gold-line);border:1px solid var(--gold-line);}
.uaeforum-root .up-card{background:var(--ink);padding:36px 32px;display:flex;flex-direction:column;gap:14px;text-decoration:none;color:inherit;cursor:pointer;transition:background .25s;position:relative;overflow:hidden;}
.uaeforum-root .up-card:hover{background:var(--ink3);}
.uaeforum-root .up-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--gold);transform:scaleX(0);transform-origin:left;transition:transform .4s cubic-bezier(.16,1,.3,1);}
.uaeforum-root .up-card:hover::before{transform:scaleX(1);}
.uaeforum-root .up-tag{font-size:9px;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:var(--gold);background:var(--gold-subtle);border:1px solid var(--gold-line);padding:4px 10px;border-radius:50px;align-self:flex-start;}
.uaeforum-root .up-title{font-family:'Cormorant Display',serif;font-size:24px;font-weight:700;color:var(--cream);line-height:1.15;letter-spacing:-.01em;}
.uaeforum-root .up-meta{font-size:11px;color:var(--stone);line-height:1.7;letter-spacing:.05em;}
.uaeforum-root .up-meta strong{color:var(--gold);font-weight:600;letter-spacing:.1em;text-transform:uppercase;}
.uaeforum-root .up-desc{font-size:12.5px;color:var(--stone);line-height:1.7;font-weight:300;flex:1;}
.uaeforum-root .up-cta{margin-top:8px;font-size:10px;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:var(--gold);}
.uaeforum-root .up-card:hover .up-cta{color:var(--cream);}

@media(max-width:980px){
  .uaeforum-root .learn-inner{grid-template-columns:1fr;}
  .uaeforum-root .org-grid{grid-template-columns:1fr;gap:24px;}
  .uaeforum-root .upcoming-grid{grid-template-columns:1fr;}
  .uaeforum-root .learn-col{padding:40px 28px;}
  .uaeforum-root .org-card{padding:32px 24px;}
}

/* CTA */
.uaeforum-root .cta-section{background:var(--ink);padding:140px 48px;text-align:center;position:relative;overflow:hidden;}
.uaeforum-root .cta-orb{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:900px;height:400px;background:radial-gradient(ellipse,rgba(200,168,75,.04) 0%,transparent 70%);pointer-events:none;}
.uaeforum-root .cta-inner{position:relative;z-index:2;}
.uaeforum-root .cta-rule{width:1px;height:60px;background:linear-gradient(to bottom,transparent,var(--gold));margin:0 auto 40px;}
.uaeforum-root .cta-h2{font-family:'Cormorant Display',serif;font-size:clamp(52px,8vw,100px);font-weight:700;line-height:.92;letter-spacing:-.03em;margin-bottom:20px;}
.uaeforum-root .cta-h2 em{font-style:italic;color:var(--gold);}
.uaeforum-root .cta-sub{font-family:'Cormorant Display',serif;font-size:clamp(18px,2vw,23px);font-style:italic;font-weight:300;color:var(--stone);max-width:520px;margin:0 auto 52px;line-height:1.55;}
.uaeforum-root .cta-actions{display:flex;gap:14px;justify-content:center;flex-wrap:wrap;}
.uaeforum-root .cta-footer-note{margin-top:44px;font-size:12px;color:var(--smoke);letter-spacing:.08em;}

/* FOOTER */
.uaeforum-root footer.uf-footer{background:var(--ink2);border-top:1px solid var(--gold-line);padding:44px 48px;}
.uaeforum-root .foot-inner{max-width:1280px;margin:0 auto;display:grid;grid-template-columns:1fr auto 1fr;gap:40px;align-items:center;}
.uaeforum-root .foot-brands{display:flex;flex-direction:column;gap:12px;}
.uaeforum-root .fb-item{display:flex;flex-direction:column;gap:2px;}
.uaeforum-root .fb-role{font-size:8px;font-weight:700;letter-spacing:.24em;text-transform:uppercase;color:var(--gold);opacity:.65;}
.uaeforum-root .fb-name{font-size:12px;color:var(--stone);font-weight:300;}
.uaeforum-root .foot-wm{text-align:center;font-family:'Cormorant Display',serif;font-size:13px;font-weight:700;color:var(--gold);letter-spacing:.14em;text-transform:uppercase;line-height:1.4;}
.uaeforum-root .foot-links{display:flex;flex-direction:column;gap:7px;align-items:flex-end;}
.uaeforum-root .foot-links a{font-size:11px;color:var(--stone);text-decoration:none;transition:color .2s;font-weight:300;}
.uaeforum-root .foot-links a:hover{color:var(--gold);}
.uaeforum-root .foot-copy{font-size:10px;color:rgba(255,255,255,.16);letter-spacing:.1em;text-transform:uppercase;margin-top:28px;text-align:center;max-width:1280px;margin-left:auto;margin-right:auto;}

/* COUNTDOWN */
.uaeforum-root .countdown-section{padding:48px 48px 0;}
.uaeforum-root .countdown-inner{max-width:1280px;margin:0 auto;display:flex;flex-wrap:wrap;justify-content:center;align-items:center;gap:24px;}
.uaeforum-root .countdown-eyebrow{font-size:10px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:var(--gold);display:flex;align-items:center;gap:10px;}
.uaeforum-root .countdown-eyebrow::before{content:'';display:block;width:24px;height:1px;background:var(--gold);opacity:.6;}
.uaeforum-root .countdown-grid{display:flex;align-items:stretch;gap:0;border:1px solid var(--gold-line);background:linear-gradient(170deg,var(--ink2) 0%,var(--ink) 100%);}
.uaeforum-root .cd-cell{padding:18px 28px;border-right:1px solid rgba(200,168,75,.12);display:flex;flex-direction:column;align-items:center;gap:4px;min-width:96px;}
.uaeforum-root .cd-cell:last-child{border-right:none;}
.uaeforum-root .cd-num{font-family:'Cormorant Display',serif;font-size:42px;font-weight:700;color:var(--gold);line-height:1;font-variant-numeric:tabular-nums;}
.uaeforum-root .cd-lbl{font-size:9px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:var(--stone);}
.uaeforum-root .cd-live{font-size:11px;font-weight:700;letter-spacing:.24em;text-transform:uppercase;color:#7CE38B;padding:14px 28px;display:flex;align-items:center;gap:10px;}
.uaeforum-root .cd-live::before{content:'';width:8px;height:8px;border-radius:50%;background:#7CE38B;animation:pulseLive 1.6s ease-in-out infinite;}
@keyframes pulseLive{0%,100%{opacity:1;}50%{opacity:.4;}}
.uaeforum-root .cal-btn{display:inline-flex;align-items:center;gap:8px;font-size:10px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:var(--gold);border:1px solid var(--gold-line);padding:13px 22px;border-radius:50px;background:transparent;cursor:pointer;transition:all .2s;}
.uaeforum-root .cal-btn:hover{background:var(--gold);color:var(--ink);}
@media(max-width:640px){
  .uaeforum-root .countdown-section{padding:36px 20px 0;}
  .uaeforum-root .cd-cell{padding:14px 16px;min-width:72px;}
  .uaeforum-root .cd-num{font-size:30px;}
}

/* MODAL */
.uaeforum-root .modal-backdrop{position:fixed;inset:0;background:rgba(7,7,10,.85);backdrop-filter:blur(8px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:20px;animation:fadeIn .25s ease-out;}
@keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
.uaeforum-root .modal-card{background:linear-gradient(170deg,var(--ink3) 0%,var(--ink2) 100%);border:1px solid var(--gold-line);max-width:640px;width:100%;max-height:90vh;overflow-y:auto;padding:48px 44px;position:relative;animation:slideUp .35s cubic-bezier(.16,1,.3,1);}
@keyframes slideUp{from{opacity:0;transform:translateY(24px);}to{opacity:1;transform:none;}}
.uaeforum-root .modal-close{position:absolute;top:18px;right:18px;width:34px;height:34px;border-radius:50%;border:1px solid rgba(200,168,75,.25);background:transparent;color:var(--gold);cursor:pointer;font-size:18px;display:flex;align-items:center;justify-content:center;transition:all .2s;}
.uaeforum-root .modal-close:hover{background:var(--gold);color:var(--ink);}
.uaeforum-root .modal-eyebrow{font-size:9px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:var(--gold);margin-bottom:14px;}
.uaeforum-root .modal-h{font-family:'Cormorant Display',serif;font-size:36px;font-weight:700;line-height:1;letter-spacing:-.02em;margin-bottom:8px;color:var(--white);}
.uaeforum-root .modal-h em{color:var(--gold);font-style:italic;}
.uaeforum-root .modal-sub{font-family:'Cormorant Display',serif;font-style:italic;font-weight:300;color:var(--stone);font-size:15px;line-height:1.5;margin-bottom:28px;}
.uaeforum-root .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
.uaeforum-root .form-grid .full{grid-column:1/-1;}
.uaeforum-root .form-field{display:flex;flex-direction:column;gap:6px;}
.uaeforum-root .form-field label{font-size:9px;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:var(--stone);}
.uaeforum-root .form-field input,.uaeforum-root .form-field select,.uaeforum-root .form-field textarea{background:var(--ink);border:1px solid rgba(200,168,75,.18);color:var(--cream);padding:13px 14px;font-size:14px;font-family:inherit;border-radius:2px;outline:none;transition:border-color .2s,box-shadow .2s;}
.uaeforum-root .form-field input:focus,.uaeforum-root .form-field select:focus,.uaeforum-root .form-field textarea:focus{border-color:var(--gold);box-shadow:0 0 0 3px rgba(200,168,75,.1);}
.uaeforum-root .form-field textarea{resize:vertical;min-height:90px;}
.uaeforum-root .form-error{color:#ff8a8a;font-size:12px;margin-top:8px;}
.uaeforum-root .form-success{margin-top:18px;padding:18px 20px;border:1px solid rgba(200,168,75,.4);background:rgba(200,168,75,.07);color:var(--cream);font-size:14px;line-height:1.6;border-radius:2px;}
.uaeforum-root .form-actions{margin-top:24px;display:flex;gap:12px;justify-content:flex-end;align-items:center;}

/* RESPONSIVE */
@media(max-width:1024px){
  .uaeforum-root nav.uf-nav{padding:0 20px;}
  .uaeforum-root .nav-inner{height:58px;}
  .uaeforum-root .nav-pill{display:none;}
  .uaeforum-root .nav-mobile-reserve{display:inline-flex;}
  .uaeforum-root .nav-logo-mark{font-size:14px;letter-spacing:.22em;}
  .uaeforum-root .nav-logo-sub{font-size:8px;letter-spacing:.18em;}
  .uaeforum-root .hero{padding:72px 20px 56px;}
  .uaeforum-root .hero-h1{font-size:clamp(44px,10vw,64px) !important;}
  .uaeforum-root .hero-photo-block{display:none;}
  .uaeforum-root .about-section,.uaeforum-root .updates-section,.uaeforum-root .why-section,.uaeforum-root .speakers-section,.uaeforum-root .agenda-section,.uaeforum-root .open-section,.uaeforum-root .sponsors-section,.uaeforum-root .faq-section,.uaeforum-root .cta-section,.uaeforum-root .learn-section,.uaeforum-root .org-section,.uaeforum-root .upcoming-section{padding:64px 20px;}
  .uaeforum-root .about-inner{grid-template-columns:1fr;gap:48px;}
  .uaeforum-root .why-grid{grid-template-columns:1fr 1fr;}
  .uaeforum-root .spk-top{grid-template-columns:1fr;gap:24px;}
  .uaeforum-root .spk-grid{grid-template-columns:1fr 1fr;}
  .uaeforum-root .spk-card.keynote{grid-column:span 2;}
  .uaeforum-root .open-grid{grid-template-columns:1fr;}
  .uaeforum-root .faq-layout{grid-template-columns:1fr;gap:40px;}
  .uaeforum-root .foot-inner{grid-template-columns:1fr;}
  .uaeforum-root .foot-links{align-items:flex-start;}
  .uaeforum-root footer.uf-footer{padding:36px 20px;}
  .uaeforum-root .announce-bar{padding:10px 16px;flex-wrap:wrap;gap:10px;font-size:10.5px;}
  .uaeforum-root .pbar{padding:14px 20px;}
  .uaeforum-root .pb{padding:0 14px;}
  .uaeforum-root .update-card{grid-template-columns:1fr;gap:18px;padding:26px 22px;}
  .uaeforum-root .uc-cta{align-items:flex-start;}
  .uaeforum-root .uc-highlights{grid-template-columns:1fr;}
  .uaeforum-root .learn-inner{grid-template-columns:1fr;}
  .uaeforum-root .org-grid{grid-template-columns:1fr;gap:24px;}
  .uaeforum-root .upcoming-grid{grid-template-columns:1fr;}
  .uaeforum-root .upcoming-head{flex-direction:column;align-items:flex-start;}
}
@media(max-width:640px){
  .uaeforum-root .why-grid,.uaeforum-root .spk-grid{grid-template-columns:1fr;}
  /* Agenda — stack time on top of content on phones */
  .uaeforum-root .ag-row{flex-direction:column;}
  .uaeforum-root .ag-time-col{min-width:0;width:100%;flex-direction:row;justify-content:flex-start;padding:14px 18px;border-right:0;border-bottom:1px solid rgba(200,168,75,.12);}
  .uaeforum-root .ag-content{padding:18px 20px;}
  /* Update cards (forum updates) — stack on phones */
  .uaeforum-root .update-card{grid-template-columns:1fr;gap:14px;padding:22px 20px;}
  .uaeforum-root .spk-card.keynote{grid-column:span 1;}
  .uaeforum-root .stats-grid{grid-template-columns:1fr;}
  .uaeforum-root .hero-h1{font-size:clamp(40px,11vw,56px) !important;}
  .uaeforum-root .hero-bottom{flex-direction:column;width:100%;}
  .uaeforum-root .hb-cell{border-right:none;border-bottom:1px solid rgba(200,168,75,.12);width:100%;padding:14px 0;}
  .uaeforum-root .hb-cell:last-child{border-bottom:none;}
  .uaeforum-root .hero-date-badge{display:none;}
  .uaeforum-root .form-grid{grid-template-columns:1fr;}
  .uaeforum-root .modal-card{padding:32px 22px;width:calc(100vw - 32px);max-width:100%;}
  .uaeforum-root .uaf-countdown,.uaeforum-root .hero-actions{flex-wrap:wrap;}
  .uaeforum-root .hero-actions > button{width:100%;}
  .uaeforum-root .ann-sep,.uaeforum-root .announce-bar .ann-sep{display:none;}
  .uaeforum-root .nav-logo-mark{font-size:12px;}
  .uaeforum-root .nav-logo-sub{display:none;}
}
.uaeforum-root .nav-mobile-reserve{display:none;background:var(--gold);color:var(--ink);border:none;font-size:10px;font-weight:800;letter-spacing:.16em;text-transform:uppercase;padding:10px 16px;border-radius:50px;cursor:pointer;}
`;

// ---------------- Registration Modal ----------------
const RegistrationModal = ({ open, onClose, defaultJoinAs = 'attendee', defaultTitle, defaultSubtitle, defaultPrefill }) => {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    country: '',
    role: '',
    organization: '',
    interest_track: '',
    investor_type: '',
    join_as: defaultJoinAs,
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [payProcessing, setPayProcessing] = useState(false);
  const [vipPromo, setVipPromo] = useState(null); // {valid, code, expires_label}

  // Detect ?promo=VIPGUEST from URL on open + validate against backend
  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams(window.location.search);
    const code = (params.get('promo') || '').trim().toUpperCase();
    if (!code) { setVipPromo(null); return; }
    axios.get(`${API}/payments/forum-ticket/promo-status?code=${encodeURIComponent(code)}`)
      .then((r) => {
        if (r.data?.submitted_code_valid) {
          setVipPromo({ valid: true, code: r.data.code, expires_label: r.data.expires_label });
        } else {
          setVipPromo({ valid: false, code });
        }
      })
      .catch(() => setVipPromo(null));
  }, [open]);

  const redeemPromo = async () => {
    setError('');
    setPayProcessing(true);
    try {
      const { data } = await axios.post(`${API}/payments/forum-ticket/redeem-promo`, {
        promo_code: vipPromo.code,
        email: form.email,
        full_name: form.full_name,
        registration_id: success?.registration?.id || success?.id || null,
      });
      setSuccess((prev) => ({
        ...(prev || {}),
        duplicate: false,
        message: data.message || 'VIP Guest seat confirmed. Check your inbox.',
        vip_redeemed: true,
      }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not redeem promo. Please try again.');
    } finally {
      setPayProcessing(false);
    }
  };

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        join_as: defaultJoinAs,
        full_name: defaultPrefill?.full_name || f.full_name,
        email: defaultPrefill?.email || f.email,
      }));
      setError('');
      setSuccess(null);
      setPayProcessing(false);
    }
  }, [open, defaultJoinAs, defaultPrefill]);

  if (!open) return null;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const isPaying = (form.join_as || 'attendee') === 'attendee';

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.full_name.trim() || !form.email.trim()) {
      setError('Please provide your full name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/events/${SLUG}/register`, form);
      setSuccess(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const startStripeCheckout = async () => {
    setError('');
    setPayProcessing(true);
    try {
      // Sandbox/test mode toggle: append ?test=1 to URL or set localStorage('stripe_test_mode')='1'
      const urlParams = new URLSearchParams(window.location.search);
      const testMode = urlParams.get('test') === '1' || localStorage.getItem('stripe_test_mode') === '1';
      const endpoint = `${API}/payments/checkout/forum-ticket${testMode ? '?test=1' : ''}`;
      const { data } = await axios.post(endpoint, {
        origin_url: window.location.origin,
        email: form.email,
        full_name: form.full_name,
        registration_id: success?.registration?.id || success?.id || null,
      });
      if (data?.url) {
        window.location.href = data.url; // Redirect to Stripe-hosted checkout
        return;
      }
      setError('Could not start payment. Please try again.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not start payment. Please try again.');
    } finally {
      setPayProcessing(false);
    }
  };

  const titleText = vipPromo?.valid ? 'VIP Guest · Free Entry' : (defaultTitle || 'Reserve Your Seat');
  const subText = vipPromo?.valid
    ? `Your VIPGUEST code is valid — your AED 149 ticket is on the house. Fill in your name and email below, then click "Redeem VIP Guest Seat" to confirm. Promo expires ${vipPromo.expires_label}.`
    : (defaultSubtitle || "AED 149 per person (~£30 / $40). Investors, speakers and partners attend complimentary — pick your role below. We'll email a payment link, the live link, the recording, and your complimentary UAE Investor Intelligence guide.");

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="event-register-modal-backdrop">
      <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="event-register-modal">
        <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="event-register-modal-close">×</button>
        <div className="modal-eyebrow">Invest UAE Forum 2026</div>
        <h3 className="modal-h">{titleText.split(' ').slice(0, -1).join(' ')} <em>{titleText.split(' ').slice(-1)[0]}</em></h3>
        <p className="modal-sub">{subText}</p>

        {success ? (
          <div className="form-success" data-testid="event-register-success">
            <strong style={{ color: 'var(--gold)' }}>{success.duplicate ? 'Already registered.' : 'You\'re in.'}</strong>
            <br />
            {success.message}
            {isPaying && !success.duplicate && !success.vip_redeemed && (
              <div style={{ marginTop: 22, padding: '18px 20px', background: vipPromo?.valid ? 'rgba(200,168,75,0.18)' : 'rgba(200,168,75,0.08)', border: '1px solid rgba(200,168,75,0.3)', borderLeft: '3px solid var(--gold)' }}>
                {vipPromo?.valid ? (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 8 }}>🎁 VIP Guest · Free Entry Code Applied</div>
                    <p style={{ margin: '0 0 14px', fontSize: 14, lineHeight: 1.6, color: 'var(--ink)' }}>
                      Your <strong>VIPGUEST</strong> code is valid — your AED 149 ticket is on the house. Promo expires <strong>{vipPromo.expires_label}</strong>. Click below to confirm your seat instantly.
                    </p>
                    {error && <div className="form-error" data-testid="event-vip-error" style={{ marginBottom: 12 }}>{error}</div>}
                    <button
                      type="button"
                      className="btn-fill"
                      onClick={redeemPromo}
                      disabled={payProcessing}
                      data-testid="event-vip-redeem-btn"
                      style={{ background: 'var(--gold)', color: 'var(--ink)', border: 'none' }}
                    >
                      {payProcessing ? 'Confirming…' : '🎁 Redeem VIP Guest Seat — Free →'}
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 8 }}>Final Step · Confirm Your Seat</div>
                    <p style={{ margin: '0 0 14px', fontSize: 14, lineHeight: 1.6, color: 'var(--ink)' }}>
                      Pay <strong>AED 149</strong> securely via Stripe to lock in your seat. You'll be redirected to a hosted checkout — Apple Pay, Google Pay, and major cards accepted.
                    </p>
                    {error && <div className="form-error" data-testid="event-pay-error" style={{ marginBottom: 12 }}>{error}</div>}

                    {/* Embedded Apple Pay / Google Pay (express checkout) — mobile/wallet conversion lane */}
                    <div style={{ marginBottom: 14 }} data-testid="event-express-pay-wrap">
                      <ForumExpressPay
                        email={form.email}
                        fullName={form.full_name}
                        registrationId={success?.registration?.id || success?.id || null}
                        onError={(msg) => setError(msg)}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 12px' }}>
                      <div style={{ flex: 1, height: 1, background: 'rgba(13,27,42,.12)' }} />
                      <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.22em', color: 'rgba(13,27,42,.5)', textTransform: 'uppercase' }}>or pay by card</span>
                      <div style={{ flex: 1, height: 1, background: 'rgba(13,27,42,.12)' }} />
                    </div>

                    <button
                      type="button"
                      className="btn-fill"
                      onClick={startStripeCheckout}
                      disabled={payProcessing}
                      data-testid="event-pay-now-btn"
                    >
                      {payProcessing ? 'Redirecting to Stripe…' : 'Pay AED 149 — Confirm Seat →'}
                    </button>
                  </>
                )}
              </div>
            )}
            <div className="form-actions" style={{ marginTop: 18 }}>
              <button type="button" className="btn-line" onClick={onClose} data-testid="event-register-success-close">{isPaying && !success.duplicate ? 'I\'ll pay later' : 'Close'}</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="form-grid" data-testid="event-register-form">
            <div className="form-field">
              <label htmlFor="ufr-name">Full Name *</label>
              <input id="ufr-name" type="text" value={form.full_name} onChange={update('full_name')} required data-testid="event-register-name" />
            </div>
            <div className="form-field">
              <label htmlFor="ufr-email">Email *</label>
              <input id="ufr-email" type="email" value={form.email} onChange={update('email')} required data-testid="event-register-email" />
            </div>
            <div className="form-field">
              <label htmlFor="ufr-phone">Phone (optional)</label>
              <input id="ufr-phone" type="tel" value={form.phone} onChange={update('phone')} placeholder="+44 ..." data-testid="event-register-phone" />
            </div>
            <div className="form-field">
              <label htmlFor="ufr-country">Country</label>
              <input id="ufr-country" type="text" value={form.country} onChange={update('country')} placeholder="United Kingdom" data-testid="event-register-country" />
            </div>
            <div className="form-field">
              <label htmlFor="ufr-role">Role / Title</label>
              <input id="ufr-role" type="text" value={form.role} onChange={update('role')} placeholder="Investor / Founder / Advisor" data-testid="event-register-role" />
            </div>
            <div className="form-field">
              <label htmlFor="ufr-org">Organization</label>
              <input id="ufr-org" type="text" value={form.organization} onChange={update('organization')} placeholder="Family Office, Fund, Firm…" data-testid="event-register-org" />
            </div>
            <div className="form-field">
              <label htmlFor="ufr-track">Investment Interest</label>
              <select id="ufr-track" value={form.interest_track} onChange={update('interest_track')} data-testid="event-register-interest">
                <option value="">— Select —</option>
                <option>Real Estate</option>
                <option>Ultra-Luxury Property</option>
                <option>AI IP & Web4</option>
                <option>Robotics & Hardware</option>
                <option>Free Zone Setup</option>
                <option>Golden Visa Pathway</option>
                <option>Governance & Compliance</option>
                <option>General — All Tracks</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="ufr-join">Joining As</label>
              <select id="ufr-join" value={form.join_as} onChange={update('join_as')} data-testid="event-register-joinas">
                <option value="attendee">Attendee</option>
                <option value="investor">Accredited Investor</option>
                <option value="speaker" disabled>Online Speaker — Slot Full</option>
                <option value="panelist">Online Panelist</option>
                <option value="guest_expert">Guest Expert</option>
                <option value="startup">Startup Spotlight</option>
                <option value="partner">Institutional Partner</option>
                <option value="sponsor">Sponsor Inquiry</option>
              </select>
            </div>
            <div className="form-field full">
              <label htmlFor="ufr-msg">Message (optional)</label>
              <textarea id="ufr-msg" value={form.message} onChange={update('message')} placeholder="Anything specific you'd like to discuss or hear about" data-testid="event-register-message" />
            </div>
            {error && <div className="form-error full" data-testid="event-register-error">{error}</div>}
            <div className="form-actions full">
              <button type="button" className="btn-line" onClick={onClose} data-testid="event-register-cancel">Cancel</button>
              <button type="submit" className="btn-fill" disabled={submitting} data-testid="event-register-submit">
                {submitting ? 'Submitting…' : 'Confirm Registration'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ---------------- Countdown Timer ----------------
const EVENT_START_UTC = new Date('2026-05-31T17:00:00Z'); // 6 PM BST = 5 PM UTC (BST = UTC+1)
const EVENT_END_UTC = new Date('2026-05-31T19:00:00Z');   // 8 PM BST

const Countdown = ({ onAddToCalendar }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const diffMs = EVENT_START_UTC.getTime() - now.getTime();
  const live = now >= EVENT_START_UTC && now <= EVENT_END_UTC;
  const past = now > EVENT_END_UTC;

  if (past) {
    return (
      <section className="countdown-section">
        <div className="countdown-inner">
          <div className="countdown-eyebrow">Event Concluded</div>
          <button className="cal-btn" onClick={onAddToCalendar} data-testid="event-recording-btn">Watch Recording</button>
        </div>
      </section>
    );
  }

  if (live) {
    return (
      <section className="countdown-section">
        <div className="countdown-inner">
          <div className="cd-live" data-testid="event-live-indicator">Live Now · Join Us</div>
          <a className="btn-fill" href="https://us06web.zoom.us/j/84373516075?pwd=9fhqBBuUJFwYQDcv0gM2drjBlV6az8.1" target="_blank" rel="noreferrer" data-testid="event-join-live">Join the Forum on Zoom →</a>
        </div>
      </section>
    );
  }

  const days = Math.floor(diffMs / 86400000);
  const hours = Math.floor((diffMs % 86400000) / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  const pad = (n) => String(n).padStart(2, '0');

  return (
    <section className="countdown-section">
      <div className="countdown-inner">
        <div className="countdown-eyebrow">Countdown to Forum</div>
        <div className="countdown-grid" data-testid="event-countdown">
          <div className="cd-cell"><div className="cd-num">{pad(days)}</div><div className="cd-lbl">Days</div></div>
          <div className="cd-cell"><div className="cd-num">{pad(hours)}</div><div className="cd-lbl">Hours</div></div>
          <div className="cd-cell"><div className="cd-num">{pad(minutes)}</div><div className="cd-lbl">Minutes</div></div>
          <div className="cd-cell"><div className="cd-num">{pad(seconds)}</div><div className="cd-lbl">Seconds</div></div>
        </div>
        <button className="cal-btn" onClick={onAddToCalendar} data-testid="event-add-calendar-btn">+ Add to Calendar</button>
      </div>
    </section>
  );
};

// Build a downloadable .ics file (RFC 5545)
const buildICS = () => {
  const dt = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//SKI AXIS//Invest UAE Forum 2026//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    'UID:invest-uae-forum-2026@skiaxis.com',
    `DTSTAMP:${dt(new Date())}`,
    `DTSTART:${dt(EVENT_START_UTC)}`,
    `DTEND:${dt(EVENT_END_UTC)}`,
    'SUMMARY:Invest UAE Forum 2026',
    'DESCRIPTION:Where UK-based & global investors unlock the full potential of the Emirates. Real Estate · Free Zones · AI Innovation · Governance · Capital Markets. Hosted by SKI Asia Pacific FZCO.',
    'LOCATION:Online · Zoom + LinkedIn Live',
    'URL:https://skiaxis.com/events/invest-uae-forum-2026',
    'STATUS:CONFIRMED',
    'BEGIN:VALARM',
    'TRIGGER:-PT1H',
    'ACTION:DISPLAY',
    'DESCRIPTION:Invest UAE Forum starts in 1 hour',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return lines.join('\r\n');
};

const downloadICS = () => {
  const blob = new Blob([buildICS()], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'invest-uae-forum-2026.ics';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ---------------- Page ----------------
export default function InvestUAEForum2026() {
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState({ joinAs: 'attendee', title: 'Reserve Your Seat', subtitle: undefined });
  const [payBanner, setPayBanner] = useState(null); // { state: 'processing'|'success'|'cancelled'|'failed', message, sessionId }
  const rootRef = useRef(null);

  useSEO({
    title: "Invest UAE Forum 2026 · Online Investment Webinar · 31 May 2026 | SKI AXIS",
    description: "Join the Invest UAE Forum 2026 — online investment webinar on Sunday 31 May 2026 with a senior UAE policy briefing. UK family offices, UAE investors, cross-border capital, IFZA free zone insights. AED 149 entry, investors complimentary.",
    keywords: "UAE investment forum 2026, invest UAE online forum, Dubai investment webinar, UAE family office investment, cross-border capital UAE, IFZA investor programme, free zone investment UAE, golden visa investor UAE, Dubai investment event 2026, UAE policy event",
    canonical: "/events/invest-uae-forum-2026",
    ogType: "article",
    ogImage: "/og-forum.png",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Event",
      "name": "Invest UAE Forum 2026",
      "description": "Online investment webinar with senior UAE policy briefing. Real estate, governance innovation, cross-border capital, IFZA free zones, AI investments — for UK family offices, UAE investors, and global capital allocators.",
      "startDate": "2026-05-31T18:00+01:00",
      "endDate": "2026-05-31T20:00+01:00",
      "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
      "eventStatus": "https://schema.org/EventScheduled",
      "location": {
        "@type": "VirtualLocation",
        "url": "https://skiaxis.com/events/invest-uae-forum-2026",
      },
      "image": ["https://skiaxis.com/og-forum.png"],
      "organizer": {
        "@type": "Organization",
        "name": "SKI Asia Pacific FZCO",
        "url": "https://skiaxis.com",
      },
      "offers": [
        { "@type": "Offer", "name": "Attendee Seat", "price": "149", "priceCurrency": "AED", "availability": "https://schema.org/InStock", "url": "https://skiaxis.com/events/invest-uae-forum-2026", "validFrom": "2026-02-01" },
        { "@type": "Offer", "name": "Investor Seat (Complimentary)", "price": "0", "priceCurrency": "AED", "availability": "https://schema.org/InStock", "url": "https://skiaxis.com/events/invest-uae-forum-2026", "validFrom": "2026-02-01" },
      ],
      "performer": (event?.speakers || []).slice(0, 10).map(sp => ({
        "@type": "Person",
        "name": sp.name,
        "jobTitle": sp.role,
      })),
    },
  });

  // Fetch event data; fallback to embedded if backend unavailable
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/events/${SLUG}`)
      .then(({ data }) => { if (mounted) setEvent(data); })
      .catch(() => { if (mounted) setEvent(EMBEDDED_FALLBACK); });
    return () => { mounted = false; };
  }, []);

  // Reveal-on-scroll
  useEffect(() => {
    if (!event || !rootRef.current) return;
    const els = rootRef.current.querySelectorAll('.reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.08, rootMargin: '0px 0px -30px 0px' });
    els.forEach((el) => io.observe(el));
    requestAnimationFrame(() => {
      els.forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('visible');
      });
    });
    return () => io.disconnect();
  }, [event]);

  const openRegister = (joinAs = 'attendee', title = 'Reserve Your Seat', subtitle) => {
    setModalContext({ joinAs, title, subtitle });
    setModalOpen(true);
  };

  // Stripe return-from-checkout handler — polls payment status, shows banner
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentParam = params.get('payment');
    const sessionId = params.get('session_id');
    const resumeTx = params.get('resume');

    // ── 1-click abandonment-recovery resume ──
    if (resumeTx) {
      (async () => {
        try {
          const { data } = await axios.get(`${API}/payments/abandonment/resume/${encodeURIComponent(resumeTx)}`);
          if (data?.already_paid) {
            window.location.replace(`/forum/thank-you?session_id=${encodeURIComponent(resumeTx)}`);
            return;
          }
          if (data?.email) {
            // Pre-fill the form, auto-open the registration modal in "attendee" mode so the buyer
            // can immediately tap Apple Pay / Google Pay or the Stripe Checkout fallback.
            setModalContext({
              joinAs: 'attendee',
              title: 'Welcome Back — Complete Your Seat',
              subtitle: `Your AED ${(data.amount || 149).toFixed(0)} seat is held. Tap to confirm and we'll send the calendar invite + receipt to ${data.email}.`,
              prefill: { email: data.email, full_name: data.full_name || '' },
            });
            setModalOpen(true);
          }
        } catch { /* silently ignore — stale resume link */ }
        // Strip resume query param so a refresh doesn't reopen the modal
        window.history.replaceState({}, '', window.location.pathname);
      })();
      return;
    }

    if (paymentParam === 'cancelled') {
      setPayBanner({ state: 'cancelled', message: 'Payment cancelled. Your seat is still pending — you can complete payment any time.' });
      // Strip query params from URL
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    if (paymentParam !== 'success' || !sessionId) return;

    // Redirect any legacy/direct hits to the new editorial Thank You page.
    window.location.replace(`/forum/thank-you?session_id=${encodeURIComponent(sessionId)}`);
  }, []);

  const data = event;
  if (!data) {
    return (
      <div ref={rootRef} className="uaeforum-root" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{STYLES}</style>
        <div style={{ color: 'var(--gold)', fontFamily: "'Cormorant Display', serif", fontSize: 24, letterSpacing: '0.2em', textTransform: 'uppercase' }}>Loading Forum…</div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="uaeforum-root" data-testid="invest-uae-forum-page">
      {/* SEO + Open Graph + Twitter Card (React 19 hoists these into <head>) */}
      <title>Invest UAE Forum 2026 · 2-Hour Live Investment Intelligence Briefing · AED 149 · SKI AXIS</title>
      <meta name="description" content="AED 149 · 2-hour live online forum on 31 May 2026. UAE investment policy, Dubai real estate (6–9% yields), AI IP, Web4, Robotics, Free Zones (IFZA + 40+ partners), Golden Visa & Governance. 11 expert speakers covering the UAE 2026 capital landscape. Investors, speakers, sponsors and partners attend complimentary." />
      <meta name="keywords" content="UAE investment, Dubai real estate, Golden Visa, Free Zone UAE, AI IP, Web4, capital markets UAE, SKI APAC, SKI Axis, Invest UAE Forum 2026" />
      <link rel="canonical" href="https://skiaxis.com/events/invest-uae-forum-2026" />
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Invest UAE Forum 2026 — Live Investment Intelligence Briefing · AED 149" />
      <meta property="og:description" content="31 May 2026 · Live Online · AED 149 / person. Senior UAE policy briefing, ultra-luxury real estate, AI innovation, governance & capital markets — for UK-based and global investors. Investors, speakers and partners attend complimentary." />
      <meta property="og:url" content="https://skiaxis.com/events/invest-uae-forum-2026" />
      <meta property="og:site_name" content="SKI AXIS" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Invest UAE Forum 2026 · 31 May · AED 149" />
      <meta name="twitter:description" content="Where UK-based & global investors unlock the full potential of the Emirates. 11 speakers, 2 hours, AED 149 (~£30 / $40)." />
      {/* JSON-LD Event schema for Google rich results */}
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Event",
        "name": "Invest UAE Forum 2026",
        "description": "Free 2-hour live online investment intelligence briefing for UK-based and global investors. UAE policy, Dubai real estate, AI/Web4, Free Zones, Golden Visa, Governance.",
        "startDate": "2026-05-31T18:00:00+01:00",
        "endDate": "2026-05-31T20:00:00+01:00",
        "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
        "eventStatus": "https://schema.org/EventScheduled",
        "location": {
          "@type": "VirtualLocation",
          "url": "https://skiaxis.com/events/invest-uae-forum-2026"
        },
        "organizer": {
          "@type": "Organization",
          "name": "SKI Asia Pacific FZCO",
          "url": "https://skiasiapacific.com"
        },
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "GBP",
          "availability": "https://schema.org/InStock",
          "url": "https://skiaxis.com/events/invest-uae-forum-2026"
        }
      })}</script>

      <style>{STYLES}</style>
      {/* Cormorant + DM Sans fonts */}
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Display:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400;1,600;1,700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,300;1,9..40,400&display=swap" rel="stylesheet" />

      {/* ANNOUNCE */}
      <div className="announce-bar">
        <div className="announce-dot" />
        <span>Live Online Forum · {data.date_label} · All Seats</span>
        <span className="announce-badge">AED 149</span>
        <span>{data.platform} · {data.doors_open}</span>
        <div className="announce-dot" />
      </div>

      {/* SITE-WIDE URGENCY TICKER */}
      <UrgencyTicker />

      {/* MOBILE-ONLY FLOATING PAY BAR (one-tap Apple Pay / Google Pay) */}
      <ForumFloatingPayBar />

      {/* NAV */}
      <nav className="uf-nav">
        <div className="nav-inner">
          <div className="nav-brand">
            <div>
              <div className="nav-logo-mark">INVEST UAE FORUM</div>
              <div className="nav-logo-sub">2026 · London Edition</div>
            </div>
          </div>
          <div className="nav-pill">
            <a href="#about" data-testid="nav-about">· About</a>
            <div className="nav-pill-sep" />
            <a href="#updates" data-testid="nav-updates">· Updates</a>
            <div className="nav-pill-sep" />
            <a href="#why" data-testid="nav-why">· Why Attend</a>
            <div className="nav-pill-sep" />
            <a href="#speakers" data-testid="nav-speakers">· Speakers</a>
            <div className="nav-pill-sep" />
            <a href="#agenda" data-testid="nav-agenda">· Agenda</a>
            <button onClick={() => window.open('https://us06web.zoom.us/j/84373516075?pwd=9fhqBBuUJFwYQDcv0gM2drjBlV6az8.1', '_blank', 'noreferrer')} className="nav-cta" data-testid="nav-register-btn">Join Zoom · 31 May</button>
          </div>
          {/* Mobile-only Reserve button */}
          <button onClick={() => window.open('https://us06web.zoom.us/j/84373516075?pwd=9fhqBBuUJFwYQDcv0gM2drjBlV6az8.1', '_blank', 'noreferrer')} className="nav-mobile-reserve" data-testid="nav-mobile-reserve">Join Zoom · 31 May</button>
        </div>
      </nav>

      {/* PAYMENT STATUS BANNER (shown when returning from Stripe) */}
      {payBanner && (
        <div
          data-testid={`pay-banner-${payBanner.state}`}
          style={{
            position: 'sticky', top: 0, zIndex: 50,
            background: payBanner.state === 'success' ? 'linear-gradient(135deg, #0D1B2A 0%, #1A2F4A 100%)'
                       : payBanner.state === 'processing' ? '#0D1B2A'
                       : payBanner.state === 'cancelled' ? '#3A4A5C'
                       : '#7a1f1f',
            color: '#FAF7F0',
            padding: '14px 24px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
            fontSize: 14, fontWeight: 500, letterSpacing: '.02em',
            borderBottom: '3px solid var(--gold)',
            textAlign: 'center',
          }}
        >
          <span style={{
            display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
            background: payBanner.state === 'success' ? '#3fbf6e' : payBanner.state === 'processing' ? 'var(--gold)' : payBanner.state === 'cancelled' ? '#FAF7F0' : '#FAF7F0',
            animation: payBanner.state === 'processing' ? 'pulse 1.4s infinite' : 'none',
            flexShrink: 0,
          }} />
          <span>{payBanner.message}</span>
          <button
            onClick={() => setPayBanner(null)}
            data-testid="pay-banner-dismiss"
            style={{ background: 'transparent', border: '1px solid rgba(250,247,240,0.35)', color: '#FAF7F0', padding: '4px 12px', fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', marginLeft: 12 }}
          >Dismiss</button>
        </div>
      )}

      {/* HERO */}
      <section className="hero" id="home">
        <div className="hero-bg">
          <div className="hero-grid" />
          <div className="hero-glow-r" />
          <div className="hero-glow-b" />
        </div>
        <div className="hero-photo-block">
          <div className="hero-photo-pattern" />
          <div className="hero-photo-line" />
          <div className="hero-mono">UAE</div>
          <div className="hero-date-badge">
            <div className="hdb-label">Doors Open</div>
            <div className="hdb-val">{data.doors_open}</div>
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(200,168,75,.28)' }}>
              <div className="hdb-label" style={{ marginBottom: 8 }}>Across Time Zones</div>
              <div style={{ display: 'grid', gap: 6, fontSize: 12.5, lineHeight: 1.4, color: '#FBF8EE' }}>
                <div><strong style={{ color: '#C8A84B', display: 'inline-block', width: 80 }}>UK</strong>{data.london_time}</div>
                <div><strong style={{ color: '#C8A84B', display: 'inline-block', width: 80 }}>UAE</strong>{data.dubai_time}</div>
                <div><strong style={{ color: '#C8A84B', display: 'inline-block', width: 80 }}>India</strong>{data.india_time || '10:30 PM – 12:30 AM IST'}</div>
                <div><strong style={{ color: '#C8A84B', display: 'inline-block', width: 80 }}>US East</strong>{data.ny_time || '1:00 – 3:00 PM EDT'}</div>
                <div><strong style={{ color: '#C8A84B', display: 'inline-block', width: 80 }}>US West</strong>{data.la_time || '10:00 AM – 12:00 PM PDT'}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-content">
          <div className="hero-eyebrow">SKI Asia Pacific FZCO Presents</div>
          <h1 className="hero-h1">
            <span className="hero-line-block">The UAE</span>
            <span className="hero-line-block"><em>Investment</em></span>
            <span className="hero-line-block">Forum 2026</span>
          </h1>
          <p className="hero-desc">{data.description}</p>
          <div className="hero-actions">
            <a className="btn-fill" href="https://us06web.zoom.us/j/84373516075?pwd=9fhqBBuUJFwYQDcv0gM2drjBlV6az8.1" target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }} data-testid="hero-register-btn">Join Zoom · 31 May →</a>
            <a href="#speakers" className="btn-line" data-testid="hero-faculty-btn">Meet the Faculty</a>
            <button onClick={downloadICS} className="btn-line" data-testid="hero-add-calendar-btn">+ Add to Calendar</button>
          </div>
          <div className="hero-bottom">
            <div className="hb-cell"><div className="hb-lbl">Live In</div><div className="hb-val">Online · Global</div></div>
            <div className="hb-cell"><div className="hb-lbl">Date</div><div className="hb-val g">{data.date_label}</div></div>
            <div className="hb-cell"><div className="hb-lbl">London</div><div className="hb-val">{data.london_time}</div></div>
            <div className="hb-cell"><div className="hb-lbl">Dubai</div><div className="hb-val">{data.dubai_time}</div></div>
            <div className="hb-cell"><div className="hb-lbl">Entry</div><div className="hb-val g">{data.entry}</div></div>
          </div>
        </div>
      </section>

      {/* PARTNER TICKER */}
      <div className="pbar">
        <div className="pbar-inner">
          <div className="pb"><span className="pb-r">Organizer</span><span className="pb-n">{data.host_org}</span></div>
          <div className="pb"><span className="pb-r">Accelerated by</span><span className="pb-n">{data.accelerated_by}</span></div>
          <div className="pb"><span className="pb-r">Powered by</span><span className="pb-n">{data.powered_by}</span></div>
          <div className="pb"><span className="pb-r">Business Consultant</span><span className="pb-n">{data.consultant}</span></div>
        </div>
      </div>

      {/* COUNTDOWN + ADD TO CALENDAR */}
      <Countdown onAddToCalendar={downloadICS} />

      {/* ABOUT */}
      <section className="about-section" id="about">
        <div className="about-inner">
          <div className="about-left reveal">
            <div className="about-pretag">About the Forum</div>
            <h2 className="about-h2">Where the World's Capital <em>Meets the UAE</em></h2>
            <p className="about-intro">Beyond the public markets lies a deeper intelligence.</p>
            <p className="about-p">The UAE has fundamentally transformed the rules of international investment. <strong>Zero capital gains tax.</strong> Over 40 free zones. A Golden Visa property pathway. <strong>6–9% gross rental yields.</strong> An AI and innovation ecosystem rivalling Singapore and London. A federal policy framework actively courting global capital.</p>
            <p className="about-p">The Invest UAE Forum 2026 is the definitive live intelligence briefing for UK-based and global investors — a senior UAE policy briefing, ultra-luxury real estate leaders, AI innovators, governance architects, and cross-border capital experts. One high-impact 2-hour session.</p>
            <p className="about-p">Organised by SKI Asia Pacific FZCO — Dubai's governance innovation firm — this forum is built not to sell you property, but to give you the <strong>institutional intelligence to invest with confidence.</strong></p>
          </div>
          <div className="about-right reveal d2">
            <div className="stats-grid">
              {data.stats.map((s, i) => (
                <div key={i} className="stat-box">
                  <span className="stat-n">{s.value}</span>
                  <span className="stat-l">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* UPDATES — NEW SECTION */}
      <section className="updates-section" id="updates">
        <div className="updates-decor" />
        <div className="updates-inner">
          <div className="section-header reveal">
            <div className="s-eyebrow">Latest Intelligence Drops</div>
            <h2 className="s-h2">Forum <em>Update Sessions</em></h2>
            <p className="s-sub">Time-sensitive policy &amp; market shifts that will be discussed live by our faculty. Read first — register to ask follow-ups during the live Q&amp;A.</p>
            <div className="s-divider" />
          </div>

          <div className="updates-list" data-testid="updates-list">
            {(data.updates || []).map((u) => (
              <article key={u.id} className="update-card reveal" data-testid={`update-card-${u.id}`}>
                <div className="uc-date">
                  <div className="uc-date-month">{u.date.split(' ')[0] || u.date}</div>
                  <div style={{ fontSize: 11, color: 'var(--stone)', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
                    {u.date.split(' ').slice(1).join(' ') || ''}
                  </div>
                  <span className="uc-tag">{u.tag}</span>
                </div>
                <div className="uc-body">
                  <h3 className="uc-headline" dangerouslySetInnerHTML={{ __html: u.headline.replace(/(no longer|seamless|UAE)/i, (m) => `<em>${m}</em>`) }} />
                  <p className="uc-lead">{u.lead}</p>
                  {u.highlights && u.highlights.length > 0 && (
                    <ul className="uc-highlights">
                      {u.highlights.map((h, i) => <li key={i}>{h}</li>)}
                    </ul>
                  )}
                  {u.speaker_attribution && <div className="uc-attribution">— {u.speaker_attribution}</div>}
                </div>
                <div className="uc-cta">
                  <span className="uc-cta-pill">{u.cta_label}</span>
                  <button className="btn-line" onClick={() => openRegister('attendee', 'Register to Discuss Live', `Submit your follow-up questions on "${u.headline}" during the live Q&A.`)} data-testid={`update-cta-${u.id}`} style={{ padding: '10px 20px' }}>Register to Ask Live →</button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="why-section" id="why">
        <div className="why-inner">
          <div className="section-header centered reveal">
            <div className="s-eyebrow">Why Attend</div>
            <h2 className="s-h2">8 Reasons to Be <em>In the Room</em></h2>
            <div className="s-divider" />
          </div>
          <div className="why-grid">
            {data.why_attend.map((w, i) => (
              <div key={w.n} className={`why-card reveal ${i % 4 === 1 ? 'd1' : i % 4 === 2 ? 'd2' : i % 4 === 3 ? 'd3' : ''}`}>
                <div className="why-num">{w.n}</div>
                <div className="why-heading">{w.h}</div>
                <div className="why-body">{w.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU'LL LEARN + WHO SHOULD ATTEND */}
      <section className="learn-section" id="learn">
        <div className="learn-inner">
          <div className="learn-col reveal">
            <div className="s-eyebrow">What You'll Learn</div>
            <h2 className="s-h2">Five Concrete <em>Takeaways</em></h2>
            <ul className="learn-list">
              {[
                <><strong>How to enter the UAE as an investor</strong> — the exact compliance, AML/KYC, and ESG architecture you need before you deploy a single dirham of capital.</>,
                <><strong>The 3 Free Zones that matter</strong> for cross-border real estate, AI innovation, and capital markets — and how zero capital-gains tax is structured legally for UK & global investors.</>,
                <><strong>Real estate frameworks that survive cycles</strong> — Abu Nahyan Al Nuaimi's structured-investing playbook for 6–9% gross yields, beyond headline-driven speculation.</>,
                <><strong>SKI APAC's continuous governance intelligence</strong> — Dr. Ross Rafahël's measurement architecture that redefines governance as a measurable, executable system for cross-border capital.</>,
                <><strong>Where AI & Web4 capital is actually flowing</strong> in the UAE in 2026 — innovation tracks rivalling Singapore and London.</>,
                <><strong>Live Q&A access</strong> — bring your questions; our faculty answers them directly during the 20-minute open floor.</>,
              ].map((t, i) => (
                <li key={i} className="learn-item">
                  <span className="learn-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="learn-text">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="learn-col reveal d1">
            <div className="s-eyebrow">Who Should Attend</div>
            <h2 className="s-h2">Built for <em>Decision-Makers</em></h2>
            <ul className="learn-list">
              {[
                { h: 'UK & European Family Offices', b: 'Allocators evaluating UAE as a Gen-2 jurisdiction for diversification beyond London real estate.' },
                { h: 'Global Investors & HNWIs', b: 'High-net-worth individuals seeking institutional-grade UAE exposure — real estate, free zones, or AI ventures.' },
                { h: 'Real Estate Strategists', b: 'Brokers, advisors, and portfolio managers serving cross-border buyers entering Dubai & Abu Dhabi.' },
                { h: 'Governance & Compliance Leaders', b: 'CFOs, GCs, and compliance officers structuring cross-border vehicles aligned with ESG/SDG mandates.' },
                { h: 'Founders & Innovation Builders', b: 'Tech, AI, and Web4 founders evaluating UAE free zones as their next operating jurisdiction.' },
                { h: 'Diplomatic & Policy Advisors', b: 'Trade attachés, embassy advisors, and ministry-level officials shaping cross-border investment frameworks.' },
              ].map((p, i) => (
                <li key={i} className="learn-item" data-testid={`audience-${i}`}>
                  <span className="learn-num">→</span>
                  <span className="learn-text"><strong>{p.h}</strong> — {p.b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SPEAKERS */}
      <section className="speakers-section" id="speakers">
        <div className="spk-inner">
          <div className="spk-top reveal">
            <div className="spk-top-left">
              <div className="s-eyebrow">Forum Faculty</div>
              <h2 className="s-h2">Speakers &amp; <em>Panelists</em></h2>
            </div>
            <div className="spk-top-right">A selected group of globally recognised leaders and practitioners, each bringing a precise, high-value perspective on UAE investment opportunities, governance architecture, and cross-border capital deployment in 2026.</div>
          </div>
          <div className="spk-grid">
            {data.speakers.map((s, i) => {
              const clickable = !!s.slug;
              const onCardClick = () => { if (clickable) navigate(`/events/invest-uae-forum-2026/speakers/${s.slug}`); };
              return (
                <div
                  key={s.initials + i}
                  className={`spk-card reveal ${s.keynote ? 'keynote' : ''} ${i % 4 === 1 ? 'd1' : i % 4 === 2 ? 'd2' : i % 4 === 3 ? 'd3' : i % 4 === 0 && i > 0 ? 'd4' : ''}`}
                  style={clickable ? { cursor: 'pointer' } : undefined}
                  onClick={onCardClick}
                  onKeyDown={(e) => { if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onCardClick(); } }}
                  tabIndex={clickable ? 0 : -1}
                  role={clickable ? 'link' : undefined}
                  data-testid={`spk-card-${s.slug || s.initials}`}
                >
                  <div className="spk-portrait">
                    <div className="spk-portrait-pattern" />
                    <div className="spk-portrait-glow" />
                    <div className="spk-initials">{s.initials}</div>
                    {s.photo_url && (
                      <>
                        <img className="spk-photo" src={s.photo_url} alt={s.name} loading="lazy" data-testid={`spk-photo-${s.slug || s.initials}`} />
                        <div className="spk-photo-overlay" />
                      </>
                    )}
                    <div className="spk-portrait-bar" />
                  </div>
                  <div className="spk-info">
                    <span className="spk-badge">{s.badge}</span>
                    <div className="spk-name">{s.name}</div>
                    <div className="spk-org">{s.role}<br />{s.org}</div>
                    <div className="spk-track">{s.track}</div>
                    {clickable && <div className="spk-view" data-testid={`spk-view-${s.slug}`}>View profile →</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ textAlign: 'center', marginTop: 36 }} className="reveal">
            <p style={{ fontFamily: "'Cormorant Display', serif", fontSize: 18, fontStyle: 'italic', fontWeight: 300, color: 'var(--stone)' }}>+ Open seats for online speakers &amp; panelists — apply below</p>
          </div>
        </div>
      </section>

      {/* ACCELERATOR CROSSLINK BANNER (Feb 2026) */}
      <style>{`
        .acrx-section{background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;padding:72px 32px;border-top:3px solid #C8A84B;border-bottom:1px solid rgba(200,168,75,.18);position:relative;overflow:hidden;}
        .acrx-section::before{content:'';position:absolute;top:-60px;right:-60px;width:280px;height:280px;background:radial-gradient(circle,rgba(200,168,75,.12) 0%,transparent 70%);}
        .acrx-inner{max-width:1180px;margin:0 auto;display:grid;grid-template-columns:1.4fr 1fr;gap:48px;align-items:center;position:relative;}
        @media(max-width:880px){.acrx-inner{grid-template-columns:1fr;gap:24px;text-align:center;}}
        .acrx-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;display:flex;align-items:center;gap:14px;margin-bottom:14px;}
        .acrx-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
        @media(max-width:880px){.acrx-eb{justify-content:center;}}
        .acrx-h{font-family:'Cormorant Display',Georgia,serif;font-size:clamp(28px,3.6vw,42px);font-weight:700;line-height:1.1;letter-spacing:-.014em;margin:0 0 14px;color:#FAF7F0;}
        .acrx-h em{font-style:italic;color:#C8A84B;}
        .acrx-d{font-size:15px;color:rgba(250,247,240,.78);line-height:1.6;margin:0;font-weight:300;}
        .acrx-cta-col{display:flex;flex-direction:column;gap:12px;align-items:flex-start;}
        @media(max-width:880px){.acrx-cta-col{align-items:center;}}
        .acrx-cta{display:inline-flex;align-items:center;gap:10px;background:#C8A84B;color:#0D1B2A;padding:18px 32px;font-size:12px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;transition:all .2s;}
        .acrx-cta:hover{background:#FAF7F0;transform:translateY(-2px);}
        .acrx-meta{font-size:11.5px;color:rgba(250,247,240,.6);font-style:italic;font-family:'Cormorant Display',Georgia,serif;font-size:14px;letter-spacing:.04em;}

        /* Delegate testimonials placeholder */
        .delgt-section{background:#FAF7F0;padding:96px 32px;border-bottom:1px solid rgba(13,27,42,.08);}
        .delgt-inner{max-width:1180px;margin:0 auto;}
        .delgt-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;display:flex;align-items:center;gap:14px;margin-bottom:18px;}
        .delgt-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
        .delgt-h{font-family:'Cormorant Display',Georgia,serif;font-size:clamp(34px,4.5vw,48px);font-weight:700;line-height:1.06;letter-spacing:-.018em;margin:0 0 12px;color:#0D1B2A;}
        .delgt-h em{font-style:italic;color:#C8A84B;}
        .delgt-sub{font-size:14.5px;color:#3D4A5C;margin:0 0 48px;max-width:680px;font-style:italic;font-family:'Cormorant Display',Georgia,serif;font-size:18px;line-height:1.55;}
        .delgt-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:22px;}
        @media(max-width:880px){.delgt-grid{grid-template-columns:1fr;}}
        .delgt-card{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:36px 32px;min-height:240px;display:flex;flex-direction:column;justify-content:space-between;position:relative;}
        .delgt-quote{font-family:'Cormorant Display',Georgia,serif;font-size:48px;color:#C8A84B;line-height:1;letter-spacing:-.04em;margin-bottom:8px;}
        .delgt-text{font-family:'Cormorant Display',Georgia,serif;font-style:italic;font-size:18px;color:#0D1B2A;line-height:1.5;margin:0 0 22px;letter-spacing:-.005em;}
        .delgt-attrib{font-size:11px;font-weight:700;letter-spacing:.16em;color:#C8A84B;text-transform:uppercase;border-top:1px solid #E8DFC9;padding-top:14px;}
        .delgt-attrib-name{color:#0D1B2A;font-size:13px;letter-spacing:.04em;text-transform:none;font-weight:600;font-family:'DM Sans',sans-serif;margin-bottom:2px;}
        .delgt-attrib-role{color:#7A8190;font-size:11px;letter-spacing:.08em;text-transform:none;font-weight:400;}
        .delgt-pending{opacity:.55;font-style:italic;font-family:'Cormorant Display',Georgia,serif;font-size:18px;color:#7A8190;line-height:1.5;}
      `}</style>
      <section className="acrx-section" id="accelerator-crosslink" data-testid="forum-accelerator-banner">
        <div className="acrx-inner">
          <div>
            <div className="acrx-eb">Building in the UAE? Stay after the forum.</div>
            <h3 className="acrx-h">Apply to <em>SKI Axis</em> Cohort 1.</h3>
            <p className="acrx-d">Every forum delegate receives <strong style={{ color: '#C8A84B' }}>priority application review</strong> for SKI Axis Cohort 1 — the UAE's first governance-first startup accelerator. 20 founders. 12 weeks. Three-layer governance framework. <strong>July 2026.</strong></p>
          </div>
          <div className="acrx-cta-col">
            <a href="/apply" className="acrx-cta" data-testid="forum-acrx-cta">Apply Now — 20 Seats →</a>
            <div className="acrx-meta">AED 6,900 · 5 fully-funded scholarships · IFZA incorporation consultancy included <span style={{ opacity: 0.75 }}>(advisory only)</span></div>
          </div>
        </div>
      </section>

      {/* DELEGATE TESTIMONIALS — placeholder cards (Feb 2026, post-forum) */}
      <section className="delgt-section" id="delegate-testimonials" data-testid="forum-testimonials">
        <div className="delgt-inner">
          <div className="delgt-eb">Voices from the room</div>
          <h3 className="delgt-h">What delegates are <em>saying</em>.</h3>
          <p className="delgt-sub">A live archive of the conversations the Invest UAE Forum unlocks — published after each session.</p>
          <div className="delgt-grid">
            <div className="delgt-card" data-testid="delegate-testimonial-1">
              <div>
                <div className="delgt-quote">"</div>
                <p className="delgt-text delgt-pending">Delegate testimonial — coming soon, post-forum.</p>
              </div>
              <div className="delgt-attrib">
                <div className="delgt-attrib-name">Awaiting first delegate</div>
                <div className="delgt-attrib-role">Family Office · UAE / UK</div>
              </div>
            </div>
            <div className="delgt-card" data-testid="delegate-testimonial-2">
              <div>
                <div className="delgt-quote">"</div>
                <p className="delgt-text delgt-pending">Delegate testimonial — coming soon, post-forum.</p>
              </div>
              <div className="delgt-attrib">
                <div className="delgt-attrib-name">Awaiting first delegate</div>
                <div className="delgt-attrib-role">Founder · Cross-Border</div>
              </div>
            </div>
            <div className="delgt-card" data-testid="delegate-testimonial-3">
              <div>
                <div className="delgt-quote">"</div>
                <p className="delgt-text delgt-pending">Delegate testimonial — coming soon, post-forum.</p>
              </div>
              <div className="delgt-attrib">
                <div className="delgt-attrib-name">Awaiting first delegate</div>
                <div className="delgt-attrib-role">Investor · Sovereign Capital</div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* AGENDA */}
      <section className="agenda-section" id="agenda">
        <div className="agenda-inner">
          <div className="section-header centered reveal">
            <div className="s-eyebrow">Forum Programme</div>
            <h2 className="s-h2">2-Hour <em>Curated Agenda</em></h2>
            <p className="s-sub">Every speaker allocated a precise 7-minute slot. One keynote. Full moderated panel. Live Q&amp;A.</p>
            <div className="s-divider" />
          </div>
          <div className="ag-list">
            {data.agenda.map((a, i) => (
              <div key={i} className={`ag-row reveal ${a.keynote ? 'keynote-r' : ''}`}>
                <div className="ag-time-col">
                  <div className="ag-time">{a.time}</div>
                  <div className="ag-dur">{a.dur}</div>
                </div>
                <div className="ag-content">
                  <div className="ag-topic">{a.topic}</div>
                  <div className="ag-who">{a.who}</div>
                  <div className="ag-desc">{a.desc}</div>
                  {a.tags && a.tags.length > 0 && (
                    <div className="ag-tags">
                      {a.tags.map((t, ti) => <span key={ti} className="atag">{t}</span>)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPEN SEATS */}
      <section className="open-section" id="join">
        <div className="open-inner">
          <div className="section-header centered reveal">
            <div className="s-eyebrow">Open Online Seats</div>
            <h2 className="s-h2">Want to <em>Join the Forum?</em></h2>
            <div className="s-divider" />
          </div>
          <div className="open-grid">
            {data.open_seats.map((o, i) => (
              <div key={o.n} className={`open-card reveal ${i % 3 === 1 ? 'd1' : i % 3 === 2 ? 'd2' : ''}`}>
                <div className="open-n">{o.n}</div>
                <div className="open-title">{o.title}</div>
                <div className="open-desc">{o.desc}</div>
                <button className="open-cta" onClick={() => openRegister(o.join_as, o.title, `Apply for: ${o.title}. Tell us briefly why you're a fit and we'll be in touch within 48 hours.`)} data-testid={`open-cta-${o.join_as}`}>{o.cta} ↗</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPONSORS */}
      <section className="sponsors-section" id="sponsors">
        <div className="sponsors-inner">
          <div className="section-header centered reveal" style={{ marginBottom: 64 }}>
            <div className="s-eyebrow">Sponsorship</div>
            <h2 className="s-h2">Sponsors &amp; <em>Strategic Partners</em></h2>
            <p className="s-sub">Reach 500+ UK-based and global investors, family offices, and professionals. Contact skiasiapacific.com for sponsorship packages.</p>
            <div className="s-divider" />
          </div>
          <div className="tier-grp reveal">
            <div className="tier-lbl">Title Sponsors</div>
            <div className="slot-row">
              <div className="slot s-title filled" data-testid="sponsor-atlantis">
                <span className="slot-tag">Powered By</span>
                <span className="slot-name">Atlantis Real Estate Dubai</span>
              </div>
              <button className="slot s-title" onClick={() => openRegister('sponsor', 'Sponsor the Forum', 'Title Sponsor inquiry — our team will respond within 24 hours.')} data-testid="sponsor-title-open">Your Brand Here</button>
            </div>
          </div>
          <div className="tier-grp reveal">
            <div className="tier-lbl">Gold Sponsors</div>
            <div className="slot-row">
              <div className="slot s-gold filled" data-testid="sponsor-ski-apac">
                <span className="slot-tag">Host · Governance Innovation</span>
                <span className="slot-name">SKI APAC FZCO</span>
              </div>
              {[1, 2, 3].map((i) => (
                <button key={i} className="slot s-gold" onClick={() => openRegister('sponsor', 'Sponsor the Forum', 'Gold Sponsor inquiry — our team will respond within 24 hours.')} data-testid={`sponsor-gold-open-${i}`}>Gold Sponsor</button>
              ))}
            </div>
          </div>
          <div className="tier-grp reveal">
            <div className="tier-lbl">Silver Sponsors</div>
            <div className="slot-row">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} className="slot s-silver" onClick={() => openRegister('sponsor', 'Sponsor the Forum', 'Silver Sponsor inquiry — our team will respond within 24 hours.')} data-testid={`sponsor-silver-open-${i}`}>Silver</button>
              ))}
            </div>
          </div>
          <div className="tier-grp reveal">
            <div className="tier-lbl">Supporting Partners</div>
            <div className="slot-row">
              <div className="slot s-partner filled" data-testid="partner-ifza">
                <span className="slot-tag">Elite Channel</span>
                <span className="slot-name">IFZA</span>
              </div>
              <div className="slot s-partner filled" data-testid="partner-dmcc">
                <span className="slot-tag">Free Zone</span>
                <span className="slot-name">DMCC</span>
              </div>
              <div className="slot s-partner filled" data-testid="partner-meydan">
                <span className="slot-tag">Free Zone</span>
                <span className="slot-name">Meydan FZ</span>
              </div>
              <div className="slot s-partner filled" data-testid="partner-difc">
                <span className="slot-tag">Innovation</span>
                <span className="slot-name">DIFC Hub</span>
              </div>
              {[1, 2, 3].map((i) => (
                <button key={i} className="slot s-partner" onClick={() => openRegister('partner', 'Become a Supporting Partner', 'Supporting Partner inquiry — our team will respond within 24 hours.')} data-testid={`partner-open-${i}`}>Partner</button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section" id="faq">
        <div className="faq-inner">
          <div className="faq-layout">
            <div className="faq-left reveal">
              <div className="s-eyebrow">FAQ</div>
              <h2 className="s-h2">Frequently<br /><em>Asked Questions</em></h2>
              <div style={{ width: 44, height: 2, background: 'var(--gold)', opacity: 0.6, marginTop: 20 }} />
              <p style={{ marginTop: 24, fontFamily: "'Cormorant Display', serif", fontSize: 18, fontStyle: 'italic', fontWeight: 300, color: 'var(--stone)', lineHeight: 1.6 }}>Everything you need to know about the Invest UAE Forum 2026.</p>
            </div>
            <div className="faq-list">
              {data.faqs.map((f, i) => (
                <div key={i} className={`faq-item reveal ${openFaq === i ? 'open' : ''}`}>
                  <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`faq-q-${i}`}>
                    {f.q}
                    <span className="faq-icon">{openFaq === i ? '−' : '+'}</span>
                  </button>
                  {openFaq === i && <div className="faq-a">{f.a}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ORGANIZER CREDIBILITY */}
      <section className="org-section" id="organizer">
        <div className="org-inner">
          <div className="section-header centered reveal" style={{ marginBottom: 56 }}>
            <div className="s-eyebrow">Hosted By</div>
            <h2 className="s-h2">The Organizers Behind <em>Invest UAE</em></h2>
            <div className="s-divider" />
          </div>
          <div className="org-grid">
            <div className="org-card reveal">
              <div className="org-eyebrow">Host & Organizer</div>
              <h3 className="org-name">SKI Asia Pacific <em>FZCO</em></h3>
              <p className="org-tagline">A Dubai Silicon Oasis-based governance & finance innovation firm operating across research, compliance architecture, and institutional investment advisory.</p>
              <p className="org-body">
                Co-founded by Dr. Neha Dewan (CEO) and Dr. Theroshen (Ross) Rafahël (Chairman of the Executive Board), SKI APAC operates
                as a Research Lab and execution environment — converting governance theory into measurable, executable digital systems.
                Sentinel is the first applied system in this pipeline; the SKI APAC accelerator extends the same methodology to founders,
                turning early-stage ideas into structured, research-backed, scalable infrastructure.
              </p>
              <div className="org-stats">
                <div className="org-stat"><div className="org-stat-v">License 70552</div><div className="org-stat-l">DSO Registered</div></div>
                <div className="org-stat"><div className="org-stat-v">3 Continents</div><div className="org-stat-l">APAC · MENA · UK</div></div>
                <div className="org-stat"><div className="org-stat-v">NFEA</div><div className="org-stat-l">Original Research IP</div></div>
                <div className="org-stat"><div className="org-stat-v">Embassy-Grade</div><div className="org-stat-l">Governance Frameworks</div></div>
              </div>
              <a className="org-link" href="https://skiasiapacific.com" target="_blank" rel="noopener noreferrer" data-testid="org-link-skiapac">Visit skiasiapacific.com →</a>
            </div>

            <div className="org-card reveal d1">
              <div className="org-eyebrow">Accelerated By</div>
              <h3 className="org-name">SKI <em>AXIS</em></h3>
              <p className="org-tagline">The intelligence platform purpose-built for cross-border governance, founder acceleration, and institutional capital matching.</p>
              <p className="org-body">
                SKI AXIS is the digital infrastructure that powers SKI APAC's masterclasses, founder cohorts, governance scoring,
                and investor matching. Cohort 1 launches July 1, 2026 with a curated group of founders building UAE-bound ventures.
                The platform is the first-of-its-kind to combine AI-powered governance scoring with institutional-grade investor relations
                — designed in Dubai for the next decade of Asia–MENA capital flow.
              </p>
              <div className="org-stats">
                <div className="org-stat"><div className="org-stat-v">Cohort 1</div><div className="org-stat-l">Launch · July 1, 2026</div></div>
                <div className="org-stat"><div className="org-stat-v">AI-Scored</div><div className="org-stat-l">GPT-Powered Apps</div></div>
                <div className="org-stat"><div className="org-stat-v">2-Tier</div><div className="org-stat-l">Founder & Investor</div></div>
                <div className="org-stat"><div className="org-stat-v">UAE-Built</div><div className="org-stat-l">Dubai Silicon Oasis</div></div>
              </div>
              <Link className="org-link" to="/" data-testid="org-link-skiaxis">Explore skiaxis.com →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" id="register">
        <div className="cta-orb" />
        <div className="cta-inner">
          <div className="cta-rule reveal" />
          <h2 className="cta-h2 reveal">Secure Your<br /><em>Seat Today</em></h2>
          <p className="cta-sub reveal d1">Join 500+ UK-based and global investors for the definitive UAE investment intelligence forum of 2026. Complimentary. No catch. Just intelligence.</p>
          <div className="cta-actions reveal d2">
            <a className="btn-fill" href="https://us06web.zoom.us/j/84373516075?pwd=9fhqBBuUJFwYQDcv0gM2drjBlV6az8.1" target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }} data-testid="cta-register-btn">Join Zoom · 31 May →</a>
            <a href="mailto:info@skiasiapacific.com" className="btn-line">Contact SKI APAC</a>
          </div>
          <div className="cta-footer-note reveal d3">{data.date_label} &nbsp;·&nbsp; {data.london_time} &nbsp;·&nbsp; {data.platform}</div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="upcoming-section" id="upcoming">
        <div className="upcoming-inner">
          <div className="upcoming-head reveal">
            <div>
              <div className="s-eyebrow">What's Next</div>
              <h2 className="s-h2">Upcoming on <em>SKI Axis</em></h2>
            </div>
            <div className="s-sub" style={{ maxWidth: 460 }}>If 31 May doesn't work — or you want deeper engagement — these are the next gateways into the SKI Axis ecosystem.</div>
          </div>
          <div className="upcoming-grid">
            <Link to="/cohort/apply" className="up-card reveal" data-testid="upcoming-cohort">
              <span className="up-tag">Founder Programme</span>
              <h3 className="up-title">Cohort 1 — UAE Founder Programme</h3>
              <div className="up-meta"><strong>Launches</strong> July 1, 2026 · <strong>Duration</strong> 12 weeks · <strong>Mode</strong> Hybrid (Dubai + Online)</div>
              <p className="up-desc">A curated, AI-scored cohort for founders building UAE-bound ventures across AI, Web4, real estate-tech, and governance innovation. Apply now — limited seats.</p>
              <div className="up-cta">Apply now →</div>
            </Link>

            <a className="up-card reveal d1" href="mailto:info@skiasiapacific.com?subject=SKI%20APAC%20Masterclass%20Interest" data-testid="upcoming-masterclass">
              <span className="up-tag">Masterclass Series</span>
              <h3 className="up-title">Cross-Border Governance Masterclass</h3>
              <div className="up-meta"><strong>Format</strong> Live online · <strong>Duration</strong> 90 min · <strong>Faculty</strong> Dr. Neha & Dr. Ross</div>
              <p className="up-desc">A live, intensive walkthrough of SKI APAC's three-layer governance model. Designed for compliance officers, GCs, and cross-border deal architects.</p>
              <div className="up-cta">Request the next session →</div>
            </a>

            <a className="up-card reveal d2" href="mailto:info@skiasiapacific.com?subject=Forum%20Recording%20Access" data-testid="upcoming-recording">
              <span className="up-tag">On-Demand</span>
              <h3 className="up-title">Forum Recording & Intelligence Brief</h3>
              <div className="up-meta"><strong>Available</strong> 48h post-event · <strong>Format</strong> Video + 12-page brief</div>
              <p className="up-desc">Couldn't attend live? Reserve the recording and the post-forum intelligence brief — sent directly to your inbox with citations, frameworks, and key takeaways.</p>
              <div className="up-cta">Reserve the recording →</div>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="uf-footer">
        <div className="foot-inner">
          <div className="foot-brands">
            <div className="fb-item"><span className="fb-role">Organizer</span><span className="fb-name">SKI Asia Pacific FZCO · License No. 70552</span></div>
            <div className="fb-item"><span className="fb-role">Accelerated by</span><span className="fb-name">SKI Axis · skiaxis.com</span></div>
            <div className="fb-item"><span className="fb-role">Powered by</span><span className="fb-name">Atlantis Real Estate Dubai UAE</span></div>
            <div className="fb-item"><span className="fb-role">Business Consultant</span><span className="fb-name">SKI APAC Business Consultant</span></div>
          </div>
          <div className="foot-wm">Invest UAE<br />Forum 2026</div>
          <div className="foot-links">
            <a href="https://skiasiapacific.com">skiasiapacific.com</a>
            <Link to="/">skiaxis.com</Link>
            <a href="mailto:info@skiasiapacific.com">info@skiasiapacific.com</a>
          </div>
        </div>
        <div className="foot-copy">© 2026 SKI Asia Pacific FZCO · All Rights Reserved · Dubai Silicon Oasis, UAE</div>
      </footer>

      <RegistrationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultJoinAs={modalContext.joinAs}
        defaultTitle={modalContext.title}
        defaultSubtitle={modalContext.subtitle}
        defaultPrefill={modalContext.prefill}
      />
    </div>
  );
}

// Minimal embedded fallback in case backend is unreachable; mirrors backend shape.
const EMBEDDED_FALLBACK = {
  date_label: 'Sunday, 31 May 2026',
  london_time: '6:00 – 8:00 PM BST',
  dubai_time: '9:00 – 11:00 PM GST',
  india_time: '10:30 PM – 12:30 AM IST',
  ny_time: '1:00 – 3:00 PM EDT (New York)',
  la_time: '10:00 AM – 12:00 PM PDT (Los Angeles)',
  doors_open: '6:00 PM BST',
  platform: 'Zoom + LinkedIn Live',
  entry: 'AED 149',
  entry_note: '~£30 / $40 / €38',
  entry_complimentary_for: ['Investor', 'Speaker', 'Sponsor', 'Partner', 'Founder'],
  host_org: 'SKI Asia Pacific FZCO',
  powered_by: 'Atlantis Real Estate Dubai UAE',
  accelerated_by: 'SKI Axis · skiaxis.com',
  consultant: 'SKI APAC Business Consultant',
  description: "Where UK-based & global investors unlock the full potential of the Emirates — Real Estate, Free Zones, AI Innovation, Governance & Capital Markets.",
  stats: [
    { value: '12', label: 'World-Class Speakers' },
    { value: '5', label: 'Investment Tracks' },
    { value: '2 Hrs', label: 'Of Pure Intelligence' },
    { value: 'AED 149', label: 'Entry · Investors Free' },
  ],
  why_attend: [],
  speakers: [],
  agenda: [],
  open_seats: [],
  faqs: [],
  updates: [],
};
